/**
 * /admin/reports/gst — GST register, verification & returns (GSTR-1 / 3B).
 * @file src/web/features/admin/pages/reports/gst.tsx
 *
 * Four views, all auto-populated from the imported vouchers:
 * - Sales / Purchases registers (per-document, with validation issues)
 * - GSTR-1 (section-wise summary + portal JSON download)
 * - GSTR-3B (table 3.1 outward + table 4 ITC + net payable)
 * Sales & purchases are separate so each is verified on its own before filing.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowDownLeft, ArrowUp, ArrowUpRight, CloudDownload, FileText, X, Eye, ShieldAlert, GitCompare, CheckCircle2, Check, Upload, Loader2, RefreshCw, LayoutDashboard, Wrench, Undo2 } from 'lucide-react';
import { toast } from '@bloomneo/uikit';
import { SEO } from '../../../shared/components';
import { AdminPageHeader } from '../components/AdminPageHeader';
import { FileReturnPanel, GST_FILED_EVENT } from '../components/FileReturnPanel';
import { OffsetLiability } from '../components/OffsetLiability';
import { Gstr3bEditor } from '../components/Gstr3bEditor';
import { FiledClaimActions } from '../components/FiledClaimActions';
import { StageRow, type StageState } from '../components/StageRow';
import { PortalTrustNote } from '../components/PortalTrustNote';
import { filingTrack } from '../lib/filing-track';
import { PortalSessionPanel, PORTAL_SESSION_ANCHOR, focusPortalSession, type SessionInfo } from '../components/PortalSessionPanel';
import { InvoiceNumbersPanel } from '../components/InvoiceNumbersPanel';
import { PortalHistoryPanel } from '../components/PortalHistoryPanel';
import { PageLoading } from '../components/PageLoading';
import { adminFetch, adminFetchJson } from '../lib/admin-api';
import { type freshness, type PortalEntry } from '../lib/portal-status';
import { isFiled, filedWithoutArn, filedClaimed, statusLabel } from '../lib/filing-status';
import { useCompany } from '../lib/company-context';
import { GstinScope, CrossCheckPanel, HsnSummary, DocIssueSummary, ManualEntryPanel } from '../components/GstScopePanels';
import { usePeriod } from '../lib/period-context';
import { PeriodBar } from '../components/PeriodBar';
import { PickEntity } from '../components/masters/shared';
import { NotInBooksList } from '../components/NotInBooksList';
import { Gstr2bWorkbookView } from '../components/Gstr2bWorkbookView';

type Direction = 'outward' | 'inward';
type View = 'overview' | Direction | 'corrections' | 'gstr1' | 'gstr3b' | 'recon2b' | 'history';

interface Issue { code: string; message: string; severity: 'error' | 'warning' }
interface GstDoc {
  voucherId: string; number: string | null; date: string; type: string; isNote: boolean;
  supplierInvoiceNo?: string | null; supplierInvoiceDate?: string | null; supplierInvoiceNoSource?: 'tally' | 'alias' | null; reverseCharge?: boolean; sign?: 1 | -1;
  partyName: string | null; partyGstin: string | null; partyLedgerId: string | null; itemLedgerId: string | null; placeOfSupply: string | null; hsn: string | null;
  taxableValue: number; cgst: number; sgst: number; igst: number; cess: number; totalTax: number; invoiceValue: number;
  rate: number | null; interState: boolean; supplyType: string; issues: Issue[];
  /** The document split by GST rate. A mixed invoice has one line per rate. */
  lines?: { rate: number; taxableValue: number; cgst: number; sgst: number; igst: number; cess: number }[];
  rateSource?: 'items' | 'ledgers' | 'tax-ledgers' | 'single' | 'inferred' | 'unresolved';
}
interface Summary {
  count: number; taxableValue: number; cgst: number; sgst: number; igst: number; cess: number; totalTax: number; invoiceValue: number; withIssues: number;
  byRate: { rate: number | null; taxableValue: number; tax: number; count: number }[];
  bySupplyType: { supplyType: string; count: number; taxableValue: number; tax: number }[];
}
interface MissingInfoRow { code: string; label: string; severity: 'error' | 'warning'; count: number; sample: { voucherId: string; number: string | null; partyName: string | null }[] }
interface Changes { hasBaseline: boolean; baselineAt?: string; baselineDocCount?: number; added: string[]; removed: string[]; changed: string[]; unchanged: number }
interface RegisterResp { direction: Direction; companyGstin: string | null; summary: Summary; missingInfo: MissingInfoRow[]; changes: Changes; docs: GstDoc[] }
interface TaxLine { taxableValue: number; igst: number; cgst: number; sgst: number; cess: number }
interface Returns {
  gstin: string | null;
  registration?: { id: string; gstin: string; stateName: string | null; filingFrequency?: string | null } | null;
  multiRegistration?: boolean;
  unattributedCount?: number;
  unknownHsnCodes?: string[];
  /** Documents in the period that fit no document series (Table 13). */
  unassignedDocuments?: number;
  unassignedDocs?: { type: string; count: number; samples: string[] }[];
  table13Notes?: { series: string; kind: 'gaps' | 'sparse' | 'elsewhere' | 'before-start' | 'unreadable' | 'out-of-sequence'; count: number; numbers: string[]; message: string }[];
  gstr1: { fp: string; gstin: string | null; summary: { totalDocs: number; taxableValue: number; totalTax: number; invoiceValue: number; sections: { code: string; label: string; count: number; taxableValue: number; tax: number }[] }; portal: any; crossCheck?: { ok: boolean; tolerance: number; rows: { field: string; sections: number; hsnSummary: number; diff: number }[] } };
  gstr3b: {
    fp: string; gstin: string | null; outward: { taxableOutward: TaxLine; zeroRated: TaxLine; nilExempt: TaxLine };
    itc: TaxLine; netTaxPayable: TaxLine; portal?: any; needsManualEntry?: { field: string; label: string; why: string }[];
    itcSource?: '2b' | 'books'; itc2bLoadedAt?: string | null;
    pending2b?: { count: number; tax: number } | null;
    itcTable?: { impg: TaxLine; rcm: TaxLine; isd: TaxLine; oth: TaxLine; rev: TaxLine; net: TaxLine; inelg: TaxLine };
  };
}

const inr = (n: number) => (n ? n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00');


export default function GstPage() {
  const { entityId } = useCompany();
  const { from, to } = usePeriod();
  const [view, setView] = useState<View>('overview');
  /* One session for the whole screen. GSTN authenticates the TAXPAYER for six
     hours, and GSTR-1, the 2B fetch and the 3B all spend the same one — so the
     client is rung once, at the top, and everything below runs inside it.
     Stable callback: the panel reports on every load, and an unstable one would
     re-fire forever. */
  const [session, setSession] = useState<SessionInfo | null>(null);
  const onSessionStatus = useCallback((i: SessionInfo) => setSession(i), []);
  const [reg, setReg] = useState<Record<Direction, RegisterResp | null>>({ outward: null, inward: null });
  const [returns, setReturns] = useState<Returns | null>(null);
  const [filings, setFilings] = useState<FilingRow[] | null>(null);
  /* What the portal last said about each return, and when it was asked. */
  const [portal, setPortal] = useState<PortalEntry[]>([]);
  const [corrections, setCorrections] = useState<CorrectionsResp | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Which GSTIN is in scope. Null = the entity's primary; only meaningful for a
  // client registered in more than one state.
  const [regId, setRegId] = useState<string | null>(null);
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [recon, setRecon] = useState<Recon | null>(null);

  // Registers (sales/purchases)
  useEffect(() => { setReg({ outward: null, inward: null }); setReturns(null); setRecon(null); }, [entityId, from, to, regId]);
  /* A history fetch loads 2B for past months, which changes the 2B recon and
     the 3B's Table 4. Leaving that tab drops both so they are rebuilt. */
  const lastView = useRef<View>(view);
  useEffect(() => {
    if (lastView.current === 'history' && view !== 'history') { setRecon(null); setReturns(null); setFilings(null); }
    lastView.current = view;
  }, [view]);
  useEffect(() => {
    if (view !== 'outward' && view !== 'inward') return;
    if (reg[view]) return;
    let cancel = false; setError(null);
    adminFetchJson<RegisterResp>('/api/gst/register', { query: { direction: view, entityId: entityId ?? undefined, gstRegistrationId: regId ?? undefined, from: from || undefined, to: to || undefined } })
      .then((d) => !cancel && setReg((r) => ({ ...r, [view]: d })))
      .catch((e) => !cancel && setError(e instanceof Error ? e.message : 'Failed to load'));
    return () => { cancel = true; };
  }, [view, entityId, from, to, regId, reg]);

  // Returns (GSTR-1 / 3B) — one fetch serves both
  useEffect(() => {
    if (view !== 'gstr1' && view !== 'gstr3b' && view !== 'overview') return;
    if (returns) return;
    let cancel = false; setError(null);
    adminFetchJson<Returns>('/api/gst/returns', { query: { entityId: entityId ?? undefined, gstRegistrationId: regId ?? undefined, from: from || undefined, to: to || undefined } })
      .then((d) => !cancel && setReturns(d))
      .catch((e) => !cancel && setError(e instanceof Error ? e.message : 'Failed to load'));
    return () => { cancel = true; };
  }, [view, entityId, from, to, regId, returns]);

  // GSTR-2B reconciliation. Period comes from the selected range's end date —
  // 2B is published per return period, so a range spanning two months would
  // be reconciled against whichever one `to` lands in, and that is stated in
  // the header rather than silently assumed.
  const period2b = to ? to.slice(5, 7) + to.slice(0, 4) : '';
  useEffect(() => {
    if ((view !== 'recon2b' && view !== 'overview') || recon || !period2b) return;
    let cancel = false; setError(null);
    adminFetchJson<Recon>('/api/gst/2b/reconcile', { query: { entityId: entityId ?? undefined, gstRegistrationId: regId ?? undefined, from: from || undefined, to: to || undefined, period: period2b } })
      .then((d) => !cancel && setRecon(d))
      .catch((e) => !cancel && setError(e instanceof Error ? e.message : 'Failed to load'));
    return () => { cancel = true; };
  }, [view, entityId, from, to, regId, recon, period2b]);

  /* Corrections — needed by its own tab and by the overview's stage, so it is
     fetched for both and cleared whenever one is applied. */
  useEffect(() => {
    if ((view !== 'corrections' && view !== 'overview') || corrections || !entityId || !from || !to) return;
    let cancel = false;
    adminFetchJson<CorrectionsResp>('/api/gst/corrections', {
      query: { entityId, gstRegistrationId: regId ?? undefined, from, to, includeIgnored: 'true' },
    })
      .then((d) => !cancel && setCorrections(d))
      .catch((e) => !cancel && setError(e instanceof Error ? e.message : 'Failed to load'));
    return () => { cancel = true; };
  }, [view, entityId, regId, from, to, corrections]);

  /* Filing history — the only part of "where does this client stand" that the
     returns themselves cannot say, because a prepared or filed record outlives
     the numbers it was built from. */
  const loadFilings = useCallback(() => {
    if (!entityId) return () => {};
    let cancel = false;
    adminFetchJson<{ filings: FilingRow[]; portal?: PortalEntry[] }>('/api/gst/filings', { query: { entityId } })
      .then((d) => { if (cancel) return; setFilings(d.filings ?? []); setPortal(d.portal ?? []); })
      .catch(() => !cancel && setFilings([]));
    return () => { cancel = true; };
  }, [entityId]);

  useEffect(() => {
    if (view !== 'overview' || !entityId) return;
    return loadFilings();
  }, [view, entityId, loadFilings]);

  /* A portal session is open: ask the portal where this month's returns stand,
     so what the Overview says is never older than the session. Read-only, once
     per session and month; the history is read again for the new answer. */
  const asked = useRef<string>('');
  useEffect(() => {
    if (view !== 'overview' || !entityId || !session?.session || !/^\d{6}$/.test(period2b)) return;
    const key = `${entityId}|${period2b}|${session.session.openedAt ?? 'open'}`;
    if (asked.current === key) return;
    asked.current = key;
    adminFetch('/api/gst/portal/status-refresh', { method: 'POST', query: { entityId, period: period2b } })
      .then(() => loadFilings())
      .catch(() => { asked.current = ''; });
  }, [view, entityId, session, period2b, loadFilings]);

  const isReg = view === 'outward' || view === 'inward';
  const data = isReg ? reg[view as Direction] : null;
  const rows = useMemo(() => (data ? (onlyIssues ? data.docs.filter((d) => d.issues.length) : data.docs) : []), [data, onlyIssues]);

  return (
    <>
      <SEO title="GST" />
      <AdminPageHeader title="GST" breadcrumbs={[{ label: 'oddits' }, { label: 'GST' }]} />

      {/* A return is filed per GSTIN, so "All entities" has nothing to build.
          Without this the whole page rendered as zeros — every section ₹0.00,
          indistinguishable from a client who genuinely had no supplies, and
          reported as a bug more than once. */}
      {!entityId ? (
        <PickEntity what="A GST return is filed for one client's GSTIN. Pick the client whose return you are working on." />
      ) : (
      <>
      <PeriodBar />
      <GstinScope entityId={entityId ?? null} value={regId} onChange={setRegId} />

      {/* The session sits above the tabs because it belongs to all of them, not
          to whichever one happens to need it first. One line: opened once, it
          covers the 2B fetch and both filings for six hours.
          scroll-mt keeps it clear of the sticky header when something below
          sends the user up here. */}
      <div id={PORTAL_SESSION_ANCHOR} className="mb-4 scroll-mt-24 rounded-xl transition">
        <PortalSessionPanel
          entityId={entityId ?? null}
          gstRegistrationId={regId}
          heading="Portal session"
          purpose="Needed to fetch 2B or history and to file — not to review or reconcile."
          onStatus={onSessionStatus}
        />
      </div>

      <div className="mb-5 inline-flex flex-wrap rounded-xl border border-ink-200 bg-paper p-1">
        <Tab active={view === 'overview'} onClick={() => setView('overview')} icon={LayoutDashboard} label="Overview" />
        <Tab active={view === 'outward'} onClick={() => setView('outward')} icon={ArrowUpRight} label="Sales" />
        <Tab active={view === 'inward'} onClick={() => setView('inward')} icon={ArrowDownLeft} label="Purchases" />
        <Tab active={view === 'corrections'} onClick={() => setView('corrections')} icon={Wrench} label="Corrections" />
        <Tab active={view === 'gstr1'} onClick={() => setView('gstr1')} icon={FileText} label="GSTR-1" />
        <Tab active={view === 'recon2b'} onClick={() => setView('recon2b')} icon={GitCompare} label="2B recon" />
        <Tab active={view === 'gstr3b'} onClick={() => setView('gstr3b')} icon={FileText} label="GSTR-3B" />
        <Tab active={view === 'history'} onClick={() => setView('history')} icon={CloudDownload} label="Portal history" />
      </div>

      {error && <div className="rounded-2xl border border-danger-200 bg-danger-50 px-6 py-4 text-sm text-danger-700">Failed to load: {error}</div>}

      {/* Corrections */}
      {view === 'corrections' && !corrections && !error && <PageLoading label="Looking for gaps in the masters" />}
      {view === 'corrections' && corrections && (
        <CorrectionsView
          r={corrections}
          entityId={entityId ?? null}
          onChanged={() => { setCorrections(null); setRecon(null); setReturns(null); setReg({ outward: null, inward: null }); }}
        />
      )}

      {/* Overview */}
      {view === 'overview' && !returns && !error && <PageLoading label="Taking stock of the period" />}
      {view === 'overview' && returns && (
        <OverviewView r={returns} recon={recon} filings={filings} portal={portal} corrections={corrections} period={period2b} go={setView} />
      )}

      {/* Registers */}
      {isReg && !data && !error && <PageLoading label="Building GST register" />}
      {isReg && data && <RegisterView view={view as Direction} data={data} rows={rows} onlyIssues={onlyIssues} setOnlyIssues={setOnlyIssues} entityId={entityId ?? null} onChanged={() => setReg((r) => ({ ...r, [view]: null }))} />}

      {/* Returns */}
      {/* Only the return tabs fetch `returns`; on 2B recon it never arrives, so
          this used to sit at "Preparing return" forever and read as a hung
          request rather than a tab waiting on data nobody had uploaded. */}
      {(view === 'gstr1' || view === 'gstr3b') && !returns && !error && <PageLoading label="Preparing return" />}
      {view === 'gstr1' && returns && <Gstr1View r={returns} entityId={entityId ?? null} session={session} onReload={() => setReturns(null)} />}
      {view === 'gstr3b' && returns && <Gstr3bView r={returns} entityId={entityId ?? null} session={session} onOpen2b={() => setView('recon2b')} />}

      {/* History from the portal. Keyed on the registration so switching GSTIN
          starts from that GSTIN's own fetch, not the last one looked at. A run
          also loads 2B and filing status, so those are re-read on the way out. */}
      {view === 'history' && (
        <PortalHistoryPanel key={`${entityId}|${regId ?? ''}`} entityId={entityId ?? null} gstRegistrationId={regId} session={session} />
      )}

      {view === 'recon2b' && !recon && !error && <PageLoading label="Reconciling against GSTR-2B" />}
      {/* A decision or a recorded bill changes GSTR-3B's Table 4 too, so both are read again. */}
      {view === 'recon2b' && recon && <Recon2bView r={recon} period={period2b} entityId={entityId ?? null} onLoaded={() => { setRecon(null); setReturns(null); }} />}
      </>
      )}
    </>
  );
}

/* ── Corrections ────────────────────────────────────────────────────────── */

interface Correction {
  id: string; kind: string; targetId: string;
  title: string; subtitle: string | null; field: string;
  current: string | null; suggested: string | null; source: string | null;
  why: string; documents: number; taxAtStake: number;
  choices?: { value: string; label: string }[];
  href?: string | null;
}
interface CorrectionsResp {
  period: string | null; twoBLoaded: boolean;
  items: Correction[]; ignored: Correction[]; ignoredCount: number;
}

/**
 * One gap, one decision: take the value, type it, or refuse it.
 *
 * Every row here is a write to MASTERS, not a patch to this month's return —
 * fix a supplier's GSTIN once and every future reconciliation pairs on it. So
 * each action says what it will write, and where, before it writes.
 */
function CorrectionRow({ c, entityId, onChanged }: { c: Correction; entityId: string | null; onChanged: () => void }) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const value = c.suggested ?? typed.trim();

  const call = async (path: 'apply' | 'ignore' | 'restore', body: Record<string, unknown>) => {
    setBusy(true);
    try {
      const r = await adminFetchJson<any>(`/api/gst/corrections/${path}`, {
        method: 'POST', query: { entityId: entityId ?? undefined },
        body: JSON.stringify({ kind: c.kind, targetId: c.targetId, ...body }),
      });
      if (r?.ok === false) throw new Error(r.error ?? 'Could not save');
      onChanged();
    } catch (e) {
      toast.error((e as Error).message.replace(/^\d+\s/, ''));
    } finally { setBusy(false); }
  };

  const where = c.kind.startsWith('ledger.') ? 'the ledger' : c.kind.startsWith('voucher.') ? 'the voucher' : 'this client';
  /* A master fix belongs in the client's own books, so it is queued into their
     Tally. A supplier's invoice number does not: it is theirs, not ours, and it
     is held beside the voucher so the voucher keeps importing. */
  const held = c.kind === 'voucher.supplierInvoiceNo';
  const toTally = !held && c.kind === 'ledger.gstin' ? ' It is queued into the client’s Tally too.' : '';

  return (
    <div className="flex flex-wrap items-start gap-3 px-5 py-4">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink-900">{c.title}</p>
        {c.subtitle && <p className="text-xs text-ink-600">{c.subtitle}</p>}
        <p className="mt-1 text-xs text-ink-500">{c.why}</p>
        {c.source && <p className="mt-1 text-[11px] text-ink-400">{c.source}</p>}
        <p className="mt-1 text-[11px] text-ink-400">
          {c.documents} document{c.documents > 1 ? 's' : ''} this period
          {c.taxAtStake ? ` · ₹${inr(c.taxAtStake)} of tax rides on it` : ''}
        </p>
      </div>

      <div className="flex shrink-0 flex-col items-end gap-1.5">
        {c.choices?.length ? (
          /* Masters cannot answer this one. Confirming keeps the figure exactly
             as the books have it and only clears the flag; the other way out is
             fixing the entry itself, which is a different screen. */
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            {c.choices.map((ch) => (
              <ConfirmAction key={ch.value}
                label={ch.label}
                title="Confirm and clear the flag — no figure changes"
                question={`Confirm this supply is exempt? Nothing in the return moves; the flag stops asking.`}
                confirmLabel="Confirm"
                onRun={() => call('apply', { value: ch.value })}
              />
            ))}
            {c.href && (
              <Link to={c.href} className={`mt-1.5 no-underline ${ROW_BTN}`}>Open the entry</Link>
            )}
          </div>
        ) : c.suggested ? (
          <ConfirmAction
            label={`Add ${c.suggested}`}
            title={held ? `Hold ${c.suggested} beside the voucher` : `Write ${c.suggested} to ${where}`}
            question={held
              ? `Record supplier invoice no. ${c.suggested} against ${c.title}? It is held beside the voucher, which stays exactly as Tally sent it — nothing is written into the client’s Tally.`
              : `Write ${c.field} ${c.suggested} to ${c.title}?${toTally}`}
            onRun={() => call('apply', { value: c.suggested })}
          />
        ) : (
          <div className="flex items-center gap-1.5">
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={c.field}
              className="w-40 rounded-lg border border-ink-300 px-2.5 py-1 text-xs focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter"
            />
            <ConfirmAction
              label="Save"
              disabled={!value || busy}
              title={value ? `Save ${value} to ${where}` : `Type the ${c.field} first`}
              question={`Save ${c.field} ${value} to ${c.title}?${toTally}`}
              triggerClassName="inline-flex items-center gap-1 rounded-lg bg-inkblack px-2.5 py-1.5 text-[11px] font-semibold text-paper disabled:opacity-40"
              onRun={() => call('apply', { value })}
            />
          </div>
        )}
        <ConfirmAction
          label="Ignore"
          disabled={busy}
          confirmLabel="Ignore it"
          title="Stop listing this — the gap itself stays"
          question={`Stop flagging ${c.title}? Nothing in the books or the return changes; it only stops being listed here, and you can bring it back from the ignored list.`}
          triggerClassName="text-[11px] font-medium text-ink-400 underline hover:text-ink-600 disabled:opacity-40"
          onRun={() => call('ignore', { value: c.suggested })}
        />
      </div>
    </div>
  );
}

function CorrectionsView({ r, entityId, onChanged }: { r: CorrectionsResp; entityId: string | null; onChanged: () => void }) {
  const [showIgnored, setShowIgnored] = useState(false);
  const ready = r.items.filter((c) => c.suggested);
  const asks = r.items.filter((c) => !c.suggested && c.choices?.length);
  const typed = r.items.filter((c) => !c.suggested && !c.choices?.length);

  const restore = async (c: Correction) => {
    try {
      await adminFetchJson('/api/gst/corrections/restore', {
        method: 'POST', query: { entityId: entityId ?? undefined },
        body: JSON.stringify({ kind: c.kind, targetId: c.targetId }),
      });
      onChanged();
    } catch (e) { toast.error((e as Error).message.replace(/^\d+\s/, '')); }
  };

  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <Band
          n={1}
          tone={ready.length ? 'danger' : 'ok'}
          title={ready.length ? `2B can fill these in — ${ready.length}` : 'Nothing 2B can fill in'}
          hint="One click each. The value comes from the portal, not from us. A master fix goes to the masters; a supplier's invoice number is held beside the voucher. Either way every future month pairs on it."
        />
        {ready.length === 0 ? (
          <div className="flex items-center gap-2 rounded-2xl border border-ok-200 bg-ok-50/60 px-5 py-4 text-sm text-ok-700">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            {r.twoBLoaded ? 'Every value 2B holds is already in the books.' : 'No 2B is loaded for this period, so there is nothing to copy from it yet.'}
          </div>
        ) : (
          <div className="divide-y divide-ink-100 overflow-hidden rounded-2xl border border-ink-200 bg-paper">
            {ready.map((c) => <CorrectionRow key={c.id} c={c} entityId={entityId} onChanged={onChanged} />)}
          </div>
        )}
      </section>

      {asks.length > 0 && (
        <section className="space-y-4">
          <Band n={2} tone="danger" title={`Worth a look — ${asks.length}`}
            hint="No GST where the same ledger charges it elsewhere. Confirm it is exempt, or open the entry if the tax was simply missed." />
          <div className="divide-y divide-ink-100 overflow-hidden rounded-2xl border border-ink-200 bg-paper">
            {asks.map((c) => <CorrectionRow key={c.id} c={c} entityId={entityId} onChanged={onChanged} />)}
          </div>
        </section>
      )}

      <section className="space-y-4">
        <Band
          n={asks.length ? 3 : 2}
          tone={typed.length ? 'danger' : 'ok'}
          title={typed.length ? `Only you know these — ${typed.length}` : 'Nothing left to fill in'}
          hint="Nothing in the books or in 2B supplies these, so they have to be typed once."
        />
        {typed.length === 0 ? (
          <div className="flex items-center gap-2 rounded-2xl border border-ok-200 bg-ok-50/60 px-5 py-4 text-sm text-ok-700">
            <CheckCircle2 className="h-4 w-4 shrink-0" /> The masters have everything the returns need.
          </div>
        ) : (
          <div className="divide-y divide-ink-100 overflow-hidden rounded-2xl border border-ink-200 bg-paper">
            {typed.map((c) => <CorrectionRow key={c.id} c={c} entityId={entityId} onChanged={onChanged} />)}
          </div>
        )}
      </section>

      {r.ignoredCount > 0 && (
        <section className="space-y-3">
          <button onClick={() => setShowIgnored((v) => !v)} className="text-xs font-medium text-ink-500 underline hover:text-ink-700">
            {showIgnored ? 'Hide' : 'Show'} the {r.ignoredCount} you have ignored
          </button>
          {showIgnored && (
            <div className="divide-y divide-ink-100 overflow-hidden rounded-2xl border border-ink-200 bg-paper">
              {r.ignored.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 px-5 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm text-ink-700">{c.title}</p>
                    <p className="text-xs text-ink-400">{c.field}{c.suggested ? ` · 2B says ${c.suggested}` : ''}</p>
                  </div>
                  <button onClick={() => restore(c)} className={ROW_BTN}><Undo2 className="h-3 w-3" /> Put it back</button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

/* ── Overview ───────────────────────────────────────────────────────────── */

/** A row of filing history. Named apart from the page's own `Filing`, which is
 *  the prepare-and-track shape rather than what /filings returns. */
interface FilingRow {
  id: string; returnType: string; period: string; status: string;
  taxableValue: number | string | null; totalTax: number | string | null;
  arn: string | null; createdAt: string; filedAt: string | null;
  /** An ARN typed in by hand that no portal check has agreed yet. */
  arnUnconfirmed?: boolean;
  /** 'portal' = found already filed on the GST portal by a history fetch. */
  source?: string;
}

/**
 * The month as a pipeline. Each stage is a state of the WORK, not a screen:
 * the books arrive, 2B says what credit exists, the credit is reconciled, then
 * the two returns are prepared and filed. Laid out in the order it actually
 * happens — 2B before the 3B, because it decides what the 3B may claim.
 */
function OverviewView({ r, recon, filings, portal, corrections, period, go }: {
  r: Returns; recon: Recon | null; filings: FilingRow[] | null; portal: PortalEntry[]; corrections: CorrectionsResp | null;
  period: string; go: (v: View) => void;
}) {
  const g1 = r.gstr1, g3 = r.gstr3b;
  const quarterly = /quarter/i.test(r.registration?.filingFrequency ?? '');
  const forPeriod = (t: string) => (filings ?? []).filter((f) => f.returnType === t && f.period === period);
  const rowsOf = (t: string) => forPeriod(t);
  const now = new Date();
  const entryOf = (t: string) => portal.find((p) => p.returnType === t && p.period === period);
  const tax = (l: TaxLine) => l.igst + l.cgst + l.sgst + l.cess;

  /* The three stages of each return — prepared, uploaded, filed — and their
     words, from one tested place (lib/filing-track). The 3B says where its
     credit came from. */
  const t1 = filingTrack({
    label: 'GSTR-1', period, dueDay: 11, quarterly, rows: rowsOf('GSTR1'), entry: entryOf('GSTR1'), now,
    unpreparedDetail: `₹${inr(g1.summary.taxableValue)} taxable across ${g1.summary.totalDocs} documents, not yet prepared.`,
    notFiledDetail: 'Not filed with the portal yet. Filed it there directly? Record it on the GSTR-1 tab so this stops chasing you.',
  });
  const t3 = filingTrack({
    label: 'GSTR-3B', period, dueDay: 20, quarterly, rows: rowsOf('GSTR3B'), entry: entryOf('GSTR3B'), now,
    unpreparedDetail: `₹${inr(tax(g3.netTaxPayable))} payable on current figures, not yet prepared.`,
    preparedSuffix: `credit ${g3.itcSource === '2b' ? 'from 2B' : 'from the books'}`,
    notFiledDetail: 'Not filed with the portal yet. Filed it there directly? Record it on the GSTR-3B tab.',
  });
  const purchaseTax = recon?.summary.booksTotal ?? 0;

  /* Each stage answers "is this done", and when it is not, why. `blocked` means
     the work cannot honestly be called done — a missing 2B, credit at risk —
     as opposed to simply not started yet. */
  /* TWO tracks, because that is the truth of it: GSTR-1 is the outward side and
     owes nothing to 2B, while the credit side runs 2B → reconcile → 3B. Chained
     into one list, a supplier who has not filed greyed out a GSTR-1 that was due
     first and could be filed today. Both tracks start from the same books. */
  type Stage = { title: string; detail: string; done: boolean; blocked?: boolean; due?: string | null; checked?: ReturnType<typeof freshness> | null; warning?: string | null; action?: { label: string; on: () => void } };

  const booksStage: Stage = {
    title: 'Books for the period',
    detail: g1.summary.totalDocs || purchaseTax
      ? `${g1.summary.totalDocs} sales document(s) · purchase tax ₹${inr(purchaseTax)}`
      : 'Nothing imported for this period yet — sync the client’s Tally.',
    done: g1.summary.totalDocs > 0 || purchaseTax !== 0,
    action: { label: 'Open Sales', on: () => go('outward') },
  };

  const outward: Stage[] = [
    { ...t1.prepared, action: { label: 'Open GSTR-1', on: () => go('gstr1') } },
    { ...t1.uploaded, action: { label: 'Open GSTR-1', on: () => go('gstr1') } },
    { ...t1.filed, action: { label: 'Open GSTR-1', on: () => go('gstr1') } },
  ];

  /* "Reconciled" means OUR side is straight: every 2B document is in the books,
     amounts agree, supplier GSTINs are on file. Credit at risk and credit GSTN
     has blocked are the supplier's doing and the taxpayer's loss — real, shown
     below, but not work this firm can finish, so they do not hold the stage open. */
  const ourGaps = (recon?.summary.counts.missingInBooks ?? 0) + (recon?.summary.counts.mismatch ?? 0)
    + (recon?.summary.counts.noGstin ?? 0) + (recon?.summary.counts.probable ?? 0);

  const credit: Stage[] = [
    {
      title: 'GSTR-2B loaded',
      detail: recon?.portalNil
        ? 'Read from the portal, and nil — no supplier declared anything against this GSTIN that month, so there is no credit to claim.'
        : recon?.hasPortalData
          ? `${recon.lines.length} document(s) from the portal · ₹${inr(recon.summary.claimable)} claimable`
          : 'Not loaded. Until it is, the 3B claims what the books paid rather than what suppliers declared.',
      done: !!recon?.hasPortalData,
      blocked: !recon?.hasPortalData,
      due: 'GSTN generates it on the 14th of the following month.',
      action: { label: 'Load or fetch 2B', on: () => go('recon2b') },
    },
    {
      title: 'Credit reconciled',
      detail: !recon?.hasPortalData ? 'Waiting on the 2B.'
        : ourGaps === 0
          ? (recon.summary.atRisk
            ? `Our side is straight. ₹${inr(recon.summary.atRisk)} still rests on suppliers who have not filed — chase, but it does not stop the return.`
            : 'Every bill is in 2B and every 2B document is in the books.')
          : `${ourGaps} item(s) only you can fix — unrecorded bills, amount differences, missing supplier GSTINs.`,
      done: !!recon?.hasPortalData && ourGaps === 0,
      blocked: !!recon?.hasPortalData && ourGaps > 0,
      action: { label: 'Open 2B recon', on: () => go('recon2b') },
    },
    { ...t3.prepared, action: { label: 'Open GSTR-3B', on: () => go('gstr3b') } },
    { ...t3.uploaded, action: { label: 'Open GSTR-3B', on: () => go('gstr3b') } },
    { ...t3.filed, action: { label: 'Open GSTR-3B', on: () => go('gstr3b') } },
  ];

  /** Mark the first unfinished stage of a track — each track has its own. */
  const withState = (list: Stage[], offset: number) => {
    const first = list.findIndex((x) => !x.done);
    return list.map((x, i) => ({
      ...x, n: offset + i + 1,
      state: (x.done ? 'done' : x.blocked ? 'blocked' : i === first ? 'current' : 'pending') as StageState,
    }));
  };
  /* Corrections sit between the books and both returns: a missing GSTIN costs
     credit on the 2B side and mis-files a supply on the GSTR-1 side, so it
     belongs to neither track alone. */
  const pending = corrections?.items.length ?? 0;
  const fillable = corrections?.items.filter((c) => c.suggested).length ?? 0;
  const correctionsStage: Stage = {
    title: 'Master data corrections',
    detail: !corrections ? 'Checking the ledgers and vouchers…'
      : pending === 0 ? 'Nothing missing that the returns need.'
        : `${pending} gap(s) in the masters${fillable ? ` — 2B can fill ${fillable} of them in` : ', to be typed once'}.`,
    done: !!corrections && pending === 0,
    blocked: pending > 0,
    action: { label: 'Open corrections', on: () => go('corrections') },
  };

  const books = withState([booksStage, correctionsStage], 0);
  const outwardStages = withState(outward, books.length);
  const creditStages = withState(credit, books.length);
  const allStages = [...books, ...outwardStages, ...creditStages];
  const doneCount = allStages.filter((x) => x.state === 'done').length;

  /* Everything that would make this month wrong, gathered from all three tabs,
     so "where does this client stand" is answered on one screen. The stages
     say what is left to do; this says what is wrong with what is already done. */
  const todo: { title: string; detail: string; go?: View; to?: string }[] = [];
  if (!r.gstin) todo.push({ title: 'No GSTIN on this client', detail: 'A return is filed per GSTIN. Nothing can be filed without it.', to: '/admin/masters?tab=company' });
  if (recon?.summary.atRisk) todo.push({ title: `₹${inr(recon.summary.atRisk)} of input credit at risk`, detail: `${recon.summary.counts.missingIn2b} bill(s) suppliers have not declared, ${recon.summary.counts.noGstin} with no supplier GSTIN.`, go: 'recon2b' });
  if (recon?.summary.counts.missingInBooks) todo.push({ title: `${recon.summary.counts.missingInBooks} document(s) in 2B but not in the books`, detail: 'Record them and the credit becomes claimable; until then the return reverses it.', go: 'recon2b' });
  /* These are documents that fit no DOCUMENT SERIES (Table 13). It was worded
     as "not attributed to a registration" — a different problem, with its own
     count — which sent people looking for a GSTIN that was never missing. */
  if (r.unassignedDocuments) todo.push({ title: `${r.unassignedDocuments} document(s) fit no document series`, detail: `${(r.unassignedDocs ?? []).map((g) => `${g.count} ${g.type}`).join(', ') || 'They'} are left out of Table 13 until a series covers them.`, go: 'gstr1' });
  if (r.unknownHsnCodes?.length) todo.push({ title: `${r.unknownHsnCodes.length} HSN/SAC code(s) not in the GSTN master`, detail: r.unknownHsnCodes.join(', '), go: 'gstr1' });
  if (g1.crossCheck && !g1.crossCheck.ok) todo.push({ title: 'Section totals do not match the HSN summary', detail: 'GSTN validates these against each other and would reject the return.', go: 'gstr1' });

  return (
    /* The work fills the page; the status sits beside it. A tracker that is all
       short lines wastes a full-width block, and a CA reading the numbers wants
       the stage list in view at the same time rather than above it. */
    <div className="grid gap-5 lg:grid-cols-[minmax(0,6fr)_minmax(0,4fr)]">
      <div className="min-w-0 space-y-6">
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">{fmtPeriod(period)} in numbers</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Tile label="Sales" value={`₹${inr(g1.summary.taxableValue)}`} sub={`tax ₹${inr(g1.summary.totalTax)}`} />
          <Tile label="Output tax, 3.1(a)" value={`₹${inr(tax(g3.outward.taxableOutward))}`} sub={`on ₹${inr(g3.outward.taxableOutward.taxableValue)}`} />
          <Tile label="Input credit, 4C" value={`₹${inr(tax(g3.itc))}`} sub={g3.itcSource === '2b' ? 'as 2B supports it' : 'from the books — estimate'} tone={g3.itcSource === '2b' ? 'ok' : 'warn'} />
          <Tile label="Net payable" value={`₹${inr(tax(g3.netTaxPayable))}`} sub="indicative" />
        </div>
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">Needs attention{todo.length ? ` — ${todo.length}` : ''}</p>
        {todo.length === 0 ? (
          <div className="flex items-center gap-2 rounded-2xl border border-ok-200 bg-ok-50/60 px-5 py-4 text-sm text-ok-700">
            <CheckCircle2 className="h-4 w-4 shrink-0" /> Nothing outstanding for this period.
          </div>
        ) : (
          <div className="divide-y divide-ink-100 overflow-hidden rounded-2xl border border-ink-200 bg-paper">
            {todo.map((x) => (
              <div key={x.title} className="flex items-start gap-3 px-5 py-4">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warn-600" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-ink-900">{x.title}</p>
                  <p className="mt-0.5 text-sm text-ink-600">{x.detail}</p>
                </div>
                {x.to
                  ? <Link to={x.to} className="shrink-0 rounded-full border border-ink-300 bg-paper px-3 py-1.5 text-xs font-semibold text-ink-700 no-underline hover:bg-ink-50">Fix in Masters</Link>
                  : x.go && <button onClick={() => go(x.go!)} className="shrink-0 rounded-full border border-ink-300 bg-paper px-3 py-1.5 text-xs font-semibold text-ink-700 hover:bg-ink-50">Open</button>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">Filing history</p>
        <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-paper">
          {filings === null ? <p className="px-5 py-4 text-sm text-ink-500">Loading…</p>
            : filings.length === 0 ? <p className="px-5 py-4 text-sm text-ink-500">Nothing prepared or filed for this client yet.</p> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 text-left text-xs uppercase tracking-wide text-ink-400">
                  <th className="px-5 py-3 font-medium">Return</th><th className="px-5 py-3 font-medium">Period</th>
                  <th className="px-5 py-3 font-medium">Status</th><th className="px-5 py-3 text-right font-medium">Taxable</th>
                  <th className="px-5 py-3 text-right font-medium">Tax</th><th className="px-5 py-3 font-medium">ARN</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-50">
                {filings.slice(0, 8).map((f) => (
                  <tr key={f.id} className={f.period === period ? 'bg-highlighter-40/30' : ''}>
                    <td className="px-5 py-2.5 font-medium text-ink-800">{f.returnType === 'GSTR3B' ? 'GSTR-3B' : 'GSTR-1'}</td>
                    <td className="px-5 py-2.5 text-ink-700">{fmtPeriod(f.period)}</td>
                    <td className="px-5 py-2.5"><span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${isFiled(f) ? 'bg-ok-100 text-ok-700' : filedClaimed(f) ? 'bg-warn-100 text-warn-700' : 'bg-ink-100 text-ink-600'}`}>{statusLabel(f)}</span>{isFiled(f) && f.filedAt && <span className="ml-1.5 text-[11px] text-ink-400">{new Date(f.filedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums text-ink-700">{inr(Number(f.taxableValue ?? 0))}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums text-ink-700">{inr(Number(f.totalTax ?? 0))}</td>
                    <td className="px-5 py-2.5 font-mono text-[11px] text-ink-500">{f.arn ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      </div>

      <aside className="lg:sticky lg:top-6 lg:self-start">
        <div className="rounded-2xl border border-ink-200 bg-paper p-5">
          <p className="text-sm font-semibold text-ink-900">{fmtPeriod(period)} — where this client stands</p>
          <p className="mt-0.5 text-xs text-ink-500">{doneCount} of {allStages.length} stages done{quarterly ? ' · quarterly (QRMP)' : ''}</p>
          <div className="mt-3 flex items-center gap-1">
            {allStages.map((x, i) => (
              <span key={i} title={x.title}
                className={`h-1.5 flex-1 rounded-full ${x.state === 'done' ? 'bg-ok-500' : x.state === 'blocked' ? 'bg-warn-400' : x.state === 'current' ? 'bg-inkblack' : 'bg-ink-200'}`} />
            ))}
          </div>

          <div className="mt-4">
            {books.map((x, i) => (
              <StageRow key={x.title} n={x.n} title={x.title} detail={x.detail} state={x.state} due={x.due} checked={x.checked} warning={x.warning}
                action={x.action} last={i === books.length - 1} />
            ))}
          </div>

          {/* The two tracks run in parallel: GSTR-1 is due on the 11th and owes
              nothing to the credit side, which is due on the 20th. */}
          <div className="mt-4 border-t border-ink-100 pt-4">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-ink-400">Outward · GSTR-1</p>
            {outwardStages.map((x, i) => (
              <StageRow key={x.title} n={x.n} title={x.title} detail={x.detail} state={x.state} due={x.due} checked={x.checked} warning={x.warning}
                action={x.action} last={i === outwardStages.length - 1} />
            ))}
          </div>

          <div className="mt-4 border-t border-ink-100 pt-4">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-ink-400">Credit · 2B &rarr; GSTR-3B</p>
            {creditStages.map((x, i) => (
              <StageRow key={x.title} n={x.n} title={x.title} detail={x.detail} state={x.state} due={x.due} checked={x.checked} warning={x.warning}
                action={x.action} last={i === creditStages.length - 1} />
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
}

/* ── GSTR-2B reconciliation ─────────────────────────────────────────────── */

type MatchStatus = 'matched' | 'probable' | 'matched_no_ref' | 'matched_late' | 'mismatch' | 'missing_in_2b' | 'no_gstin' | 'missing_in_books' | 'itc_blocked';
interface ReconMoney { taxableValue: number; igst: number; cgst: number; sgst: number; cess: number; tax: number }
interface ReconLine {
  status: MatchStatus; section: string | null; reverseCharge: boolean;
  supplierGstin: string | null; supplierName: string | null;
  invoiceNumber: string | null; invoiceDate: string | null;
  booksRef: string | null; voucherNumber: string | null; voucherId: string | null; bookedOn: string | null;
  books: ReconMoney | null; portal: ReconMoney | null; taxDifference: number;
  supplierFiledOn: string | null; itcReason: string | null; note: string | null;
  partyLedgerId?: string | null;
  suggested?: {
    gstin: string; invoiceNumber: string; supplierName: string | null;
    invoiceDate: string | null; sameDay: boolean; portal: ReconMoney;
  } | null;
  decision?: { action: 'hold' | 'claim'; reason: string | null; decidedBy: string | null; decidedAt: string } | null;
  needsAction?: boolean;
  doc?: {
    id: string;
    invoiceValue: number;
    placeOfSupply: string | null;
    invoiceDateIso: string | null;
    itcAvailable: boolean;
    invoiceType: string | null;
    sourceType: string | null;
    irn: string | null;
    supplierPeriod: string | null;
  } | null;
}
interface Recon {
  period: string; hasPortalData: boolean; portalNil?: boolean; loadedAt: string | null;
  summary: {
    booksTotal: number; portalTotal: number; claimable: number; atRisk: number; blocked: number; reversed: number;
    reversedAwaitingGstin: number;
    heldBack?: number; claimedWithoutBooks?: number;
    needsAction?: { count: number; tax: number };
    counts: { matched: number; probable: number; matchedNoRef: number; matchedLate: number; mismatch: number; missingIn2b: number; noGstin: number; missingInBooks: number; awaitingGstin: number; itcBlocked: number; total: number };
  };
  itc3b: {
    avl: { impg: ReconMoney; isd: ReconMoney; oth: ReconMoney };
    rcm: ReconMoney; rev: { oth: ReconMoney }; inelg: { oth: ReconMoney }; net: ReconMoney;
  };
  otherCredits: { section: string; supplierGstin: string | null; supplierName: string | null; invoiceNumber: string; invoiceDate: string | null; itcAvailable: boolean; portal: ReconMoney }[];
  lines: ReconLine[];
}

/** A dot carries the severity the removed chip used to: green settled, amber
 *  worth a look, red costing money. */
const STATUS_DOT: Record<MatchStatus, string> = {
  matched: 'bg-ok-500', matched_late: 'bg-ok-500', matched_no_ref: 'bg-ok-400', probable: 'bg-ok-400',
  mismatch: 'bg-warn-500', missing_in_books: 'bg-warn-500',
  missing_in_2b: 'bg-danger-500', no_gstin: 'bg-danger-500', itc_blocked: 'bg-danger-500',
};

/**
 * The heading over a group, in the words a CA would use. It replaced the chip
 * that used to repeat on every row: a heading has room to say what the group
 * IS, and the hint says what it means for the return — which is the question
 * the group actually raises.
 */
const STATUS_HEADING: Record<MatchStatus, string> = {
  matched: 'Matched — nothing to do',
  matched_no_ref: 'Matched, but your books have no supplier invoice number',
  probable: 'Matched on amount — the invoice numbers differ',
  matched_late: 'Matched — the supplier filed late',
  mismatch: 'The books and 2B disagree on the amount',
  missing_in_2b: 'Not in 2B — the supplier has not declared it',
  no_gstin: 'The supplier ledger has no GSTIN',
  missing_in_books: 'In 2B, but not in your books',
  itc_blocked: 'GSTN has blocked this credit',
};
const STATUS_HINT: Record<MatchStatus, string> = {
  matched: 'Claimed in full.',
  matched_no_ref: 'They pair on supplier, date and amount. Add the numbers and next month pairs on the number itself.',
  probable: 'Claimed. Correct the number in the books so the pair is certain, not inferred.',
  matched_late: 'A bill from an earlier month the supplier filed only now. Claimable in this return.',
  mismatch: 'Only the lower of the two figures is claimed. Ask the supplier to amend their GSTR-1 if theirs is wrong.',
  missing_in_2b: 'Not claimable yet. Chase the supplier; if they file next month it matches then.',
  no_gstin: 'Only a registered supplier can charge GST. Until the GSTIN is on the ledger this can never match.',
  missing_in_books: 'Nothing is held back on its own. Record each bill in the books (preferred), or hold it back in 4B(2), or claim it anyway. GSTR-3B is filed only once each is decided.',
  itc_blocked: 'Reported in 4D(2) and never claimed — a cost, not a fix.',
};

/** A line needing a person's attention, as opposed to one that simply matched. */
/* What must be dealt with BEFORE filing, because each one changes what this
   month can claim. A bill matched on supplier, date and amount is not one of
   them: the credit is already claimed and the missing invoice number is
   bookkeeping to tidy afterwards. */
const NEEDS_ACTION: MatchStatus[] = ['missing_in_2b', 'no_gstin', 'itc_blocked', 'mismatch', 'missing_in_books', 'probable'];

const fmtPeriod = (p: string) => (p ? `${MONTHS[Number(p.slice(0, 2)) - 1] ?? p.slice(0, 2)} ${p.slice(2)}` : 'this period');
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Load a period's GSTR-2B by hand.
 *
 * There is no automatic fetch: 2B comes from GSTN, and pulling it needs the
 * taxpayer's own portal session. The period is the one the page is already
 * scoped to, and the server refuses a file whose own period or GSTIN says it
 * belongs somewhere else.
 */
function Load2bButton({ period, entityId, onLoaded, label = 'Load the 2B file' }: {
  period: string; entityId: string | null; onLoaded: () => void; label?: string;
}) {
  const [busy, setBusy] = useState(false);

  const load = async (file: File) => {
    setBusy(true);
    try {
      const text = await file.text();
      let payload: unknown;
      try { payload = JSON.parse(text); }
      catch { throw new Error('That file is not JSON. Download the 2B as JSON from the portal, not the Excel.'); }

      const res = await adminFetch(`/api/gst/2b/import?period=${encodeURIComponent(period)}`, {
        method: 'POST',
        query: { entityId: entityId ?? undefined },
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(out.message || `Could not load the 2B (HTTP ${res.status})`);
      const replaced = out.replaced ? ` (replaced the ${out.replaced} loaded earlier)` : '';
      toast.success(`${out.stored ?? out.parsed ?? 0} document(s) loaded for ${fmtPeriod(period)}${replaced}`);
      onLoaded();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <label className={`inline-flex cursor-pointer items-center gap-2 rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-paper transition hover:bg-inkblack-80 ${busy ? 'pointer-events-none opacity-50' : ''}`}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
      {busy ? 'Loading…' : label}
      <input
        type="file"
        accept=".json,application/json"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) load(f); e.target.value = ''; }}
      />
    </label>
  );
}

/** Table 4 as the loaded 2B supports it — the figures GSTR-3B will carry. */
function Itc3bPanel({ r }: { r: Recon }) {
  const t = r.itc3b;
  const rows: { code: string; label: string; m: ReconMoney; minus?: boolean; strong?: boolean }[] = [
    { code: '4A(1)', label: 'Import of goods', m: t.avl.impg },
    { code: '4A(4)', label: 'From an Input Service Distributor', m: t.avl.isd },
    { code: '4A(5)', label: 'All other ITC — suppliers’ invoices and notes in 2B', m: t.avl.oth },
    { code: '4B(2)', label: 'Reversed — in 2B but not supported by the books', m: t.rev.oth, minus: true },
    { code: '4C', label: 'Net ITC available', m: t.net, strong: true },
  ];
  return (
    <div className="rounded-2xl border border-ink-200 bg-paper">
      <div className="border-b border-ink-100 px-5 py-3">
        <p className="text-sm font-semibold text-ink-900">What this puts into GSTR-3B Table 4</p>
        <p className="text-xs text-ink-500">
          The GSTR-3B tab uses these figures while this 2B is loaded. Reverse-charge credit, 4A(3), comes from the books —
          it follows the tax you pay in 3.1(d).{t.inelg.oth.tax ? ` ₹${inr(t.inelg.oth.tax)} GSTN denied goes to 4D(2).` : ''}
        </p>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-ink-400">
            <th className="px-5 py-2 font-medium">Table</th><th className="px-5 py-2 font-medium" />
            <th className="px-5 py-2 text-right font-medium">IGST</th><th className="px-5 py-2 text-right font-medium">CGST</th>
            <th className="px-5 py-2 text-right font-medium">SGST</th><th className="px-5 py-2 text-right font-medium">Total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-50">
          {rows.map((x) => (
            <tr key={x.code} className={x.strong ? 'bg-ink-50/70 font-semibold text-ink-900' : 'text-ink-700'}>
              <td className="px-5 py-2 font-mono text-xs">{x.code}</td>
              <td className="px-5 py-2">{x.label}</td>
              {(['igst', 'cgst', 'sgst', 'tax'] as const).map((k) => (
                <td key={k} className="px-5 py-2 text-right tabular-nums">{x.m[k] ? `${x.minus ? '− ' : ''}${inr(x.m[k])}` : '—'}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Puts the GSTIN 2B already knows onto the party ledger, from the line that
 *  found it. The alternative was retyping a fifteen-character GSTIN into
 *  another screen, which is how the wrong one gets typed. */
/** Row-level buttons are compact rectangles, not the page's pills: a pill in a
 *  table row reads as the primary action on the screen, which a per-row fix is
 *  not, and it eats the width the row needs for its own words. */
const ROW_BTN = 'inline-flex items-center gap-1 rounded-lg border border-ink-300 bg-paper px-2.5 py-1 text-[11px] font-semibold text-ink-700 transition hover:bg-ink-50 disabled:opacity-50';

/**
 * A row action that says what it is about to write before it writes it.
 *
 * These are not view changes: they edit the client's masters and queue the same
 * edit into the client's own Tally. A single click, with the value only in a
 * tooltip, is too little between "I wonder what this does" and a write-back.
 */
function ConfirmAction({ label, question, confirmLabel = 'Save', onRun, title, triggerClassName, disabled }: {
  label: string; question: string; confirmLabel?: string; title?: string; onRun: () => Promise<void>;
  /** For triggers that are not row buttons — the quiet "Ignore" link, say. */
  triggerClassName?: string; disabled?: boolean;
}) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!asking) {
    return (
      <button onClick={() => setAsking(true)} title={title} disabled={disabled}
        className={triggerClassName ?? `ml-1.5 mt-1.5 ${ROW_BTN}`}>{label}</button>
    );
  }
  const run = async () => {
    setBusy(true);
    try { await onRun(); setAsking(false); } finally { setBusy(false); }
  };
  return (
    <span className="ml-1.5 mt-1.5 inline-flex flex-wrap items-center gap-1.5 rounded-lg border border-ink-200 bg-ink-50/70 px-2 py-1.5 text-[11px] text-ink-600">
      <span className="max-w-xs">{question}</span>
      <button onClick={run} disabled={busy} className="inline-flex items-center gap-1 rounded-md bg-inkblack px-2.5 py-1 text-[11px] font-semibold text-paper disabled:opacity-50">
        {busy && <Loader2 className="h-3 w-3 animate-spin" />} {confirmLabel}
      </button>
      <button onClick={() => setAsking(false)} disabled={busy} className="rounded-md border border-ink-300 bg-paper px-2.5 py-1 text-[11px] font-semibold text-ink-600 hover:bg-ink-50">Cancel</button>
    </span>
  );
}

function UseGstinButton({ line, entityId, onFixed }: { line: ReconLine; entityId: string | null; onFixed: () => void }) {
  if (!line.suggested || !line.partyLedgerId) return null;
  const gstin = line.suggested.gstin;
  const apply = async () => {
    try {
      await adminFetchJson(`/api/books/ledgers/${line.partyLedgerId}`, {
        method: 'PATCH', body: JSON.stringify({ entityId, gstin }),
      });
      toast.success(`${gstin} saved on ${line.supplierName ?? 'the party'} — re-matching.`);
      onFixed();
    } catch (e) {
      toast.error((e as Error).message.replace(/^\d+\s/, ''));
    }
  };
  return (
    <ConfirmAction
      label="Use this GSTIN"
      title={`Save ${gstin} on this party ledger`}
      question={`Save ${gstin} on ${line.supplierName ?? 'this party'}?`}
      onRun={apply}
    />
  );
}

/**
 * Records the supplier's invoice number from 2B beside the voucher.
 *
 * Offered only where the two sides already agree on supplier, date and amount —
 * the number is the one field the books are missing, and 2B is the authority on
 * it. Next month the pair then matches on the number itself, with no date-and-
 * amount inference.
 *
 * It goes through the corrections endpoint, not at the voucher: writing it onto
 * the voucher would mark it edited, and the loader skips edited vouchers, so
 * filling in a number here would quietly stop that voucher ever updating from
 * Tally again. The alias holds the number without touching the record.
 */
function CopyInvoiceNoButton({ line, entityId, onFixed }: { line: ReconLine; entityId: string | null; onFixed: () => void }) {
  const from2b = line.invoiceNumber;
  const fillable = line.status === 'matched_no_ref' || line.status === 'probable';
  if (!fillable || !line.voucherId || !from2b || line.booksRef === from2b) return null;
  const replacing = !!line.booksRef;
  const copy = async () => {
    try {
      const r = await adminFetchJson<any>('/api/gst/corrections/apply', {
        method: 'POST', query: { entityId: entityId ?? undefined },
        body: JSON.stringify({ kind: 'voucher.supplierInvoiceNo', targetId: line.voucherId, value: from2b }),
      });
      if (r?.ok === false) throw new Error(r.error ?? 'Could not save');
      toast.success(`Invoice ${from2b} is now held against voucher ${line.voucherNumber ?? ''}.`.replace('  ', ' '));
      onFixed();
    } catch (e) {
      toast.error((e as Error).message.replace(/^\d+\s/, ''));
    }
  };
  return (
    <ConfirmAction
      label={replacing ? `Use 2B no. ${from2b}` : 'Copy invoice no. from 2B'}
      title={replacing
        ? `Replace the recorded number "${line.booksRef}" with ${from2b}, as 2B has it`
        : `Hold ${from2b} beside this voucher, as 2B has it`}
      question={`${replacing ? `Replace "${line.booksRef}" with ${from2b}` : `Record ${from2b}`} against voucher ${line.voucherNumber ?? ''}? It is held beside the voucher, which stays exactly as Tally sent it — nothing is written into the client\u2019s Tally.`}
      onRun={copy}
    />
  );
}

/**
 * The reconciliation, as a table.
 *
 * Grouped by status, four columns. It had seven — status, invoice, supplier,
 * books, books tax, 2B tax, difference — which needed 60rem before it could be
 * read at all, repeated the same pill down every row, and spent three columns
 * on money that is identical on every settled line. The supplier, which is
 * what a person scans for, sat third.
 *
 * Now the status is a heading over its own group and carries that group's
 * count and money; the supplier leads; and the two tax columns collapse into
 * one figure unless the two sides actually disagree, which is the only time
 * the difference is worth a reader's attention.
 */
/**
 * One GSTR-2B document the books do not have, read in full.
 *
 * Everything here is the PORTAL's account of the bill — what the supplier
 * filed — shown so a CA can decide whether it is theirs before anything is
 * written. Recording it comes next; for now this only reads.
 */
interface RecordPlan {
  ok: true;
  line: { id: string; invoiceNumber: string; invoiceDate: string | null; supplierGstin: string; supplierName: string | null; taxableValue: number; cgst: number; sgst: number; igst: number; cess: number; invoiceValue: number; totalTax: number };
  party: { id: string; name: string } | null;
  createParty: { name: string; gstin: string; parent: string } | null;
  /** The same supplier already in the books under another spelling, found by
   *  name once the GSTIN found nothing. */
  sameName: { id: string; name: string; gstin: string | null; conflict: boolean } | null;
  purchaseLedgers: string[];
  suggestedPurchase: string | null;
  taxLedgers: { cgst: string | null; sgst: string | null; igst: string | null; cess: string | null };
  missingTaxLedgers: string[];
  roundOff: { amount: number; ledger: string | null } | null;
  duplicate: { id: string; number: string | null; date: string } | null;
  narration: string;
}

function Bill2bModal({ line, period, entityId, onRecorded, onClose }: {
  line: ReconLine; period: string; entityId: string | null;
  onRecorded: () => void; onClose: () => void;
}) {
  const d = line.doc;
  const p2 = line.portal;
  const tax = p2?.tax ?? 0;
  const taxable = p2?.taxableValue ?? 0;
  // The rate the invoice actually carries. 2B holds a rate per ITEM row and the
  // stored row keeps only invoice totals, so this is the effective rate — and
  // on a single-rate bill, which most are, it is the rate.
  const rate = taxable > 0 ? (tax / taxable) * 100 : null;

  const fmt = (v: string | null) => (!v ? '—' : /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10).split('-').reverse().join('-') : v);

  /* Dated before the period whose 2B carried it: the supplier filed late. The
     credit is claimable now, but the bill may already have been booked — and
     claimed — in its own month, and claiming twice is recovered with interest. */
  const iso = d?.invoiceDateIso ?? null;
  const backdated = (() => {
    if (!iso || !/^\d{6}$/.test(period)) return null;
    const pY = Number(period.slice(2)); const pM = Number(period.slice(0, 2));
    const iY = Number(iso.slice(0, 4)); const iM = Number(iso.slice(5, 7));
    if (iY * 12 + iM >= pY * 12 + pM) return null;
    return `${MONTHS[iM - 1] ?? iso.slice(5, 7)} ${iY}`;
  })();

  /* Escape closes, as every dialog should. Bound on the document because focus
     may sit on the backdrop or on nothing at all when it opens. */
  const titleId = useId();
  const [plan, setPlan] = useState<RecordPlan | null>(null);
  const [planErr, setPlanErr] = useState<string | null>(null);
  const [purchaseLedger, setPurchaseLedger] = useState('');
  const [voucherDate, setVoucherDate] = useState('');
  const [narration, setNarration] = useState('');
  const [createParty, setCreateParty] = useState(true);
  /* Default yes: putting the GSTIN on the matched ledger is the link the books
     were missing, and writing it once stops every later bill from this
     supplier arriving here unmatched. */
  const [linkParty, setLinkParty] = useState(true);
  const [received, setReceived] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [dupVoucher, setDupVoucher] = useState<{ id: string; number: string | null; date: string } | null>(null);

  const lineId = line.doc?.id ?? null;
  useEffect(() => {
    if (!lineId) return;
    adminFetchJson<RecordPlan>(`/api/gst/2b/${lineId}/record-plan`, { query: { entityId: entityId ?? undefined } })
      .then((pl) => {
        setPlan(pl);
        setPurchaseLedger(pl.suggestedPurchase ?? '');
        setVoucherDate(pl.line.invoiceDate ?? '');
        setNarration(pl.narration);
        setDupVoucher(pl.duplicate);
      })
      .catch((e) => setPlanErr(e instanceof Error ? e.message.replace(/^\d+\s/, '') : 'Could not prepare the voucher'));
  }, [lineId, entityId]);

  const save = async () => {
    if (!lineId) return;
    setSaving(true); setSaveErr(null);
    try {
      const out = await adminFetchJson<any>(`/api/gst/2b/${lineId}/record`, {
        method: 'POST',
        query: { entityId: entityId ?? undefined, period },
        body: JSON.stringify({ purchaseLedger, voucherDate, narration, createParty, linkParty, confirmed: received }),
      });
      /* Manual vouchers carry no number of their own today, so naming the bill
         is the useful confirmation — "Recorded as voucher —" says nothing. */
      toast.success(`Recorded ${plan?.line.supplierName ?? 'the bill'} inv ${plan?.line.invoiceNumber ?? ''}`.trim()
        + (out?.partyCreated ? ' · supplier ledger created' : ''));
      onRecorded();
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message.replace(/^\d+\s/, '') : 'Could not record the bill';
      setSaveErr(msg);
    } finally { setSaving(false); }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  /* The entry as it will be written, from the pieces the server already sent:
     the taxable value to the chosen purchase head, each tax to its input
     ledger, the round-off, and the whole invoice to the supplier. The server
     builds the real one the same way and checks it balances; this is so a
     person can see it before pressing the button. */
  const voucherLines = useMemo(() => {
    if (!plan || !purchaseLedger) return [] as { name: string; amount: number; cr?: boolean }[];
    const out: { name: string; amount: number; cr?: boolean }[] = [
      { name: purchaseLedger, amount: plan.line.taxableValue },
    ];
    const heads: [string | null, number][] = [
      [plan.taxLedgers.cgst, plan.line.cgst], [plan.taxLedgers.sgst, plan.line.sgst],
      [plan.taxLedgers.igst, plan.line.igst], [plan.taxLedgers.cess, plan.line.cess],
    ];
    for (const [ledger, amt] of heads) if (ledger && Math.abs(Number(amt)) >= 0.005) out.push({ name: ledger, amount: Number(amt) });
    if (plan.roundOff?.ledger && Math.abs(plan.roundOff.amount) >= 0.005) {
      out.push({ name: plan.roundOff.ledger, amount: plan.roundOff.amount, cr: plan.roundOff.amount < 0 });
    }
    const party = plan.party?.name ?? plan.sameName?.name ?? plan.createParty?.name ?? 'the supplier';
    out.push({ name: party, amount: plan.line.invoiceValue, cr: true });
    return out;
  }, [plan, purchaseLedger]);

  const drTotal = voucherLines.filter((e) => !e.cr).reduce((t, e) => t + Math.abs(e.amount), 0);
  const crTotal = voucherLines.filter((e) => e.cr).reduce((t, e) => t + Math.abs(e.amount), 0);
  const balanced = Math.abs(drTotal - crTotal) < 0.01;

  /** One figure, big enough to read without hunting for it. */
  const Fig = ({ label, value, strong = false }: { label: string; value: number; strong?: boolean }) => (
    <div className="rounded-lg bg-ink-50/70 px-2.5 py-2">
      <p className="text-[10px] uppercase tracking-wide text-ink-400">{label}</p>
      <p className={`tabular-nums ${strong ? 'text-sm font-semibold text-ink-900' : 'text-sm text-ink-800'}`}>₹{inr(value)}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-900/40" onClick={onClose} />
      {/* A column, not one scrolling box: the whole card scrolled together, so
          on a laptop — anything under about 740px tall — the buttons were cut
          off the bottom and there was no way to reach them. The figures scroll;
          the name you are deciding about and the decision itself stay put. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative z-10 flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl border border-ink-200 bg-paper shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-ink-100 px-6 py-4">
          <div className="min-w-0">
            <h3 id={titleId} className="truncate text-lg font-semibold text-ink-900">{line.supplierName ?? 'Supplier'}</h3>
            <p className="font-mono text-[11px] text-ink-400">{line.supplierGstin ?? '—'}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="shrink-0 text-ink-400 hover:text-ink-700"><X className="h-5 w-5" /></button>
        </div>

        {/* min-h-0 so this can actually shrink inside the flex column; without
            it the body refuses to scroll and pushes the footer off again. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">

        {d && !d.itcAvailable && (
          <div className="mb-4 flex items-start gap-2 rounded-xl border border-danger-200 bg-danger-50 px-3.5 py-2.5 text-xs text-danger-700">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>GSTN marks this credit unavailable{line.itcReason ? `: ${line.itcReason}` : ''}. Recording the bill will not make it claimable.</span>
          </div>
        )}

        {/* The bill in three figures, not twelve rows.
            Four of the twelve were tax heads at zero and one was a rate we
            can put beside the tax it applies to, so the numbers that matter
            were buried among numbers that did not. */}
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">The bill, as the supplier filed it</p>
        <div className="mt-1 rounded-xl border border-ink-200 px-4 py-3">
          <p className="text-sm font-medium text-ink-900">
            Invoice {line.invoiceNumber ?? '—'}{' '}
            <span className="font-normal text-ink-500">dated {fmt(iso ?? line.invoiceDate)}</span>
          </p>
          <p className="mt-0.5 text-[11px] text-ink-500">
            Filed with GSTN {line.supplierFiledOn ?? '—'}
            {d?.placeOfSupply ? ` · place of supply ${d.placeOfSupply}` : ''}
          </p>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <Fig label="Taxable" value={taxable} />
            <Fig label={rate == null ? 'Tax' : `Tax @ ${rate.toFixed(2).replace(/\.00$/, '')}%`} value={tax} strong />
            <Fig label="Invoice value" value={d?.invoiceValue ?? taxable + tax} />
          </div>
          {/* Only the heads carrying money: a row of zeroes says nothing. */}
          <p className="mt-2 text-[11px] text-ink-500">
            {([['CGST', p2?.cgst], ['SGST', p2?.sgst], ['IGST', p2?.igst], ['Cess', p2?.cess]] as [string, number | undefined][])
              .filter(([, v]) => Math.abs(Number(v ?? 0)) >= 0.005)
              .map(([h, v]) => `${h} ₹${inr(Number(v))}`).join('  ·  ') || 'No tax on this bill.'}
          </p>
        </div>

        <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-ink-400">In your books</p>
        <div className="mt-1 rounded-xl border border-warn-200 bg-warn-50/60 px-4 py-3 text-sm text-warn-800">
          No purchase voucher found in books for this invoice.
        </div>

        {backdated && (
          <div className="mt-3 flex items-start gap-2 rounded-xl border border-warn-200 bg-warn-50/70 px-3.5 py-2.5 text-xs text-warn-700">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>Invoice dated {backdated} — check it wasn&rsquo;t already claimed in that month&rsquo;s 3B.</span>
          </div>
        )}

        {/* ── the voucher this would become ─────────────────────────────── */}
        {planErr && <p className="mt-4 rounded-xl border border-danger-200 bg-danger-50 px-3.5 py-2.5 text-xs text-danger-700">{planErr}</p>}
        {!plan && !planErr && <p className="mt-4 text-xs text-ink-400">Preparing the voucher…</p>}

        {dupVoucher && (
          <div className="mt-4 rounded-xl border border-danger-200 bg-danger-50 px-3.5 py-2.5 text-xs text-danger-700">
            Already in the books as voucher {dupVoucher.number ?? '—'} dated {String(dupVoucher.date).slice(0, 10).split('-').reverse().join('-')}.
          </div>
        )}

        {plan && !dupVoucher && (
          <>
            <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-ink-400">Record as a purchase</p>
            <div className="mt-1 space-y-3 rounded-xl border border-ink-200 px-4 py-3">
              <div>
                <span className="text-xs text-ink-500">Supplier</span>
                {plan.party ? (
                  <p className="text-sm text-ink-900">{plan.party.name}</p>
                ) : plan.sameName && !plan.sameName.conflict ? (
                  /* Found by name once the GSTIN found nothing — the ordinary
                     state of a Tally book that predates anyone caring about
                     2B. Offering to "create" it collided with the ledger that
                     was there all along and left the dialog with no way out. */
                  <div className="mt-0.5 space-y-1">
                    <p className="text-sm text-ink-900">
                      <span className="mr-1.5 rounded bg-ok-50 px-1.5 py-0.5 text-[11px] font-semibold text-ok-700">Match found</span>
                      {plan.sameName.name}
                    </p>
                    <p className="text-xs text-ink-500">
                      {plan.sameName.name === (plan.line.supplierName ?? '')
                        ? 'Already in the books, but with no GSTIN on the ledger — which is why it did not match on its own.'
                        : `Already in the books under this name; the supplier filed as “${plan.line.supplierName}”.`}
                    </p>
                    <label className="flex items-start gap-2 text-xs text-ink-800">
                      <input type="checkbox" checked={linkParty} onChange={(e) => setLinkParty(e.target.checked)} className="mt-0.5" />
                      <span>Add GSTIN <span className="font-mono">{plan.line.supplierGstin}</span> to this ledger, so this supplier matches on its own from now on.</span>
                    </label>
                  </div>
                ) : plan.sameName?.conflict ? (
                  <p className="text-sm text-danger-800">
                    {plan.sameName.name}
                    <span className="mt-0.5 block text-xs">
                      The books already have this name under GSTIN {plan.sameName.gstin}, and this bill is from {plan.line.supplierGstin}.
                      Two suppliers share the name — rename one in Masters, or record this bill by hand.
                    </span>
                  </p>
                ) : (
                  <label className="mt-0.5 flex items-start gap-2 text-sm text-ink-800">
                    <input type="checkbox" checked={createParty} onChange={(e) => setCreateParty(e.target.checked)} className="mt-1" />
                    <span>Create party <span className="font-medium">{plan.createParty?.name}</span> ({plan.createParty?.gstin}) under {plan.createParty?.parent}</span>
                  </label>
                )}
              </div>

              <div>
                <span className="text-xs text-ink-500">Purchase ledger</span>
                <select
                  value={purchaseLedger}
                  onChange={(e) => setPurchaseLedger(e.target.value)}
                  className="mt-0.5 w-full rounded-lg border border-ink-200 bg-paper px-3 py-2 text-sm focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter"
                >
                  <option value="">Choose a ledger…</option>
                  {plan.purchaseLedgers.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>

              {plan.missingTaxLedgers.length > 0 && (
                <p className="flex items-start gap-1.5 text-xs text-danger-700">
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                  No input {plan.missingTaxLedgers.join(' / ')} ledger in the books — create it before recording this bill.
                </p>
              )}

              {/* The voucher, not a description of one.
                  A CA reads debits and credits; naming the tax ledgers in a
                  sentence and the round-off in another left them to picture
                  the entry themselves and hope it balanced. */}
              <div>
                <span className="text-xs text-ink-500">The voucher this writes</span>
                {purchaseLedger ? (
                  <div className="mt-1 overflow-hidden rounded-lg border border-ink-200">
                    <table className="w-full text-xs">
                      <tbody className="divide-y divide-ink-50">
                        {voucherLines.map((e, i) => (
                          <tr key={`${e.name}-${i}`} className={e.cr ? 'bg-ink-50/40' : ''}>
                            <td className={`py-1.5 pr-2 ${e.cr ? 'pl-7 text-ink-700' : 'pl-3 text-ink-900'}`}>{e.name}</td>
                            <td className="px-1 py-1.5 text-right text-[10px] font-semibold uppercase text-ink-400">{e.cr ? 'Cr' : 'Dr'}</td>
                            <td className="py-1.5 pl-2 pr-3 text-right tabular-nums text-ink-900">₹{inr(Math.abs(e.amount))}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t border-ink-200 text-[11px] font-semibold text-ink-600">
                          <td className="py-1.5 pl-3">{balanced ? 'Balances' : 'Does not balance — check the ledgers'}</td>
                          <td />
                          <td className={`py-1.5 pr-3 text-right tabular-nums ${balanced ? '' : 'text-danger-700'}`}>₹{inr(drTotal)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-ink-400">Choose a purchase ledger above and the entry appears here.</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-xs text-ink-500">Voucher date</span>
                  <input type="date" value={voucherDate} onChange={(e) => setVoucherDate(e.target.value)}
                    className="mt-0.5 w-full rounded-lg border border-ink-200 bg-paper px-3 py-2 text-sm focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter" />
                </div>
                <div>
                  <span className="text-xs text-ink-500">Supplier invoice</span>
                  <p className="mt-0.5 truncate py-2 text-sm text-ink-700">{plan.line.invoiceNumber} · {fmt(plan.line.invoiceDate)}</p>
                </div>
              </div>

              {plan.roundOff && !plan.roundOff.ledger && (
                <p className="text-xs text-danger-700">
                  Round-off of ₹{inr(Math.abs(plan.roundOff.amount))} has no Round Off ledger in the books to go to.
                </p>
              )}

              <div>
                <span className="text-xs text-ink-500">Narration</span>
                <input value={narration} onChange={(e) => setNarration(e.target.value)}
                  className="mt-0.5 w-full rounded-lg border border-ink-200 bg-paper px-3 py-2 text-sm focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter" />
              </div>

              {/* Section 16(2)(b). Nobody but the person recording it knows
                  whether the goods arrived, and credit taken before they did is
                  recovered with interest. */}
              <label className="flex items-start gap-2 text-sm text-ink-800">
                <input type="checkbox" checked={received} onChange={(e) => setReceived(e.target.checked)} className="mt-1" />
                <span>Goods/services received and invoice in hand</span>
              </label>

              <p className="text-[11px] text-ink-500">
                This bill will also be sent to Tally (without stock items). Add items there if you track inventory.
              </p>
            </div>
          </>
        )}

        {saveErr && <p className="mt-3 rounded-xl border border-danger-200 bg-danger-50 px-3.5 py-2.5 text-xs text-danger-700">{saveErr}</p>}

        </div>

        <div className="flex items-center justify-end gap-2 border-t border-ink-100 px-6 py-4">
          <button
            onClick={save}
            disabled={saving || !plan || !!dupVoucher || !received || !purchaseLedger || plan.missingTaxLedgers.length > 0}
            title={dupVoucher ? 'Already in the books' : !received ? 'Confirm the goods and invoice are in hand' : undefined}
            className="inline-flex items-center gap-2 rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Record this bill
          </button>
          <button onClick={onClose} className="rounded-full border border-ink-300 bg-paper px-5 py-2.5 text-sm font-semibold text-ink-700 hover:bg-ink-50">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Bills in 2B but not in the books, waiting for a decision. Nothing is held
 * back on its own any more: the user pushes the entries from Tally, records
 * them here, or decides per bill.
 */
function NotInBooksBanner({ count, tax, period, entityId, onDone }: {
  count: number; tax: number; period: string; entityId: string | null; onDone: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [received, setReceived] = useState(false);
  const [busy, setBusy] = useState(false);
  const recordAll = async () => {
    setBusy(true);
    try {
      const res = await adminFetch('/api/gst/2b/record-all', {
        method: 'POST', query: { entityId: entityId ?? undefined, period },
        body: JSON.stringify({ period, confirmed: true }),
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok || out.ok === false) throw new Error(out.error || out.message || `Could not record the bills (HTTP ${res.status})`);
      const left = (out.skipped ?? []) as { invoiceNumber: string; reason: string }[];
      toast.success(`Recorded ${out.recorded?.length ?? 0} bill${out.recorded?.length === 1 ? '' : 's'}${left.length ? `; ${left.length} left to do by hand` : ''}`);
      if (left.length) toast.error(left.slice(0, 3).map((x) => `${x.invoiceNumber}: ${x.reason}`).join(' · '));
      setConfirming(false); setReceived(false);
      onDone();
    } catch (e) { toast.error((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-warn-300 bg-warn-50 px-5 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-sm text-warn-900">
          <span className="font-semibold">{count} bill{count === 1 ? ' is' : 's are'} (₹{inr(tax)}) in 2B but not in the books.</span>{' '}
          Push these entries from Tally or record them here. Nothing is held back until you decide, and GSTR-3B waits for that.
        </p>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {/* The client's Tally is synced by the Oddits Sync app on their PC;
              the Import page has it. This page re-checks when you come back. */}
          <Link to="/admin/import" className="inline-flex items-center gap-1.5 rounded-full border border-warn-300 bg-paper px-3 py-1.5 text-xs font-semibold text-warn-800 no-underline hover:bg-warn-100">
            <RefreshCw className="h-3.5 w-3.5" /> Sync from Tally
          </Link>
          {!confirming && (
            <button onClick={() => setConfirming(true)} className="inline-flex items-center gap-1.5 rounded-full bg-inkblack px-3 py-1.5 text-xs font-semibold text-white">
              Record all
            </button>
          )}
        </div>
      </div>
      {confirming && (
        <div className="mt-3 space-y-2 rounded-xl border border-warn-200 bg-paper p-3 text-xs text-ink-700">
          <p>Each bill is recorded as a purchase from 2B&rsquo;s figures, to the purchase ledger its supplier&rsquo;s bills usually go to. Any that cannot be (no ledger, already booked) are left for you to record one by one.</p>
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={received} onChange={(e) => setReceived(e.target.checked)} className="mt-0.5" />
            <span>The goods or services were received and the invoices are in hand for all {count} bills (Section 16(2)).</span>
          </label>
          <div className="flex gap-2">
            <button onClick={recordAll} disabled={!received || busy} className="inline-flex items-center gap-1.5 rounded-full bg-inkblack px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Record {count} bill{count === 1 ? '' : 's'}
            </button>
            <button onClick={() => { setConfirming(false); setReceived(false); }} className="rounded-full px-3 py-1.5 text-xs text-ink-600 hover:text-ink-900">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReconTable({ lines, entityId, period, onFixed, settled = false }: {
  lines: ReconLine[]; entityId: string | null; period: string; onFixed: () => void; settled?: boolean;
}) {
  // Which 2B-only bill is open. One at a time: it is a document to read, not a
  // list to scan.
  const [openBill, setOpenBill] = useState<ReconLine | null>(null);
  const date = (d: string | null) => (!d ? '—' : /^\d{4}-\d{2}-\d{2}$/.test(d) ? d.split('-').reverse().join('-') : d);
  const pad = settled ? 'py-2' : 'py-3';

  // Keep the incoming order — the service already sorted by what matters —
  // while gathering each status into one run.
  const groups: { status: MatchStatus; rows: ReconLine[] }[] = [];
  for (const l of lines) {
    const g = groups.find((x) => x.status === l.status);
    if (g) g.rows.push(l); else groups.push({ status: l.status, rows: [l] });
  }
  const money = (l: ReconLine) => {
    const b = l.books?.tax, p2 = l.portal?.tax;
    const differs = b != null && p2 != null && Math.abs(b - p2) > 0.005;
    if (differs) {
      return (
        <>
          <span className="block tabular-nums text-ink-900">{inr(b!)}<span className="ml-1 text-[11px] font-normal text-ink-400">books</span></span>
          <span className="block tabular-nums text-ink-900">{inr(p2!)}<span className="ml-1 text-[11px] font-normal text-ink-400">2B</span></span>
          <span className="block text-[11px] font-semibold text-danger-700">{inr(Math.abs(b! - p2!))} apart</span>
        </>
      );
    }
    const only = b ?? p2;
    return (
      <>
        <span className="block tabular-nums text-ink-900">{only == null ? '—' : inr(only)}</span>
        <span className="block text-[11px] font-normal text-ink-400">{b == null ? 'in 2B only' : p2 == null ? 'in the books only' : 'both agree'}</span>
      </>
    );
  };

  return (
    <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-paper">
      <table className="w-full min-w-[40rem] text-sm">
        <thead>
          <tr className="border-b border-ink-100 text-left text-xs uppercase tracking-wide text-ink-400">
            <th className="px-5 py-2.5 font-medium">Supplier</th>
            <th className="px-5 py-2.5 font-medium">Invoice</th>
            <th className="px-5 py-2.5 font-medium">In our books</th>
            <th className="px-5 py-2.5 text-right font-medium">Tax</th>
          </tr>
        </thead>
        {groups.map((g) => {
          const total = g.rows.reduce((t, l) => t + Math.abs(l.books?.tax ?? l.portal?.tax ?? 0), 0);
          return (
            <tbody key={g.status} className="divide-y divide-ink-50 border-t border-ink-100">
              <tr className="border-y border-ink-200 bg-ink-100">
                <td colSpan={4} className="px-5 py-2.5">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                    <span className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                      <span className={`size-2 shrink-0 rounded-full ${STATUS_DOT[g.status]}`} />
                      {STATUS_HEADING[g.status]}
                    </span>
                    <span className="text-xs font-medium tabular-nums text-ink-600">
                      {g.rows.length} bill{g.rows.length > 1 ? 's' : ''} · ₹{inr(total)}
                    </span>
                  </div>
                  <p className="mt-0.5 max-w-3xl text-xs text-ink-500">{STATUS_HINT[g.status]}</p>
                </td>
              </tr>
              {g.rows.map((l, i) => (
                <tr key={i} className="align-top hover:bg-ink-50/60">
                  <td className={`px-5 ${pad}`}>
                    <span className="font-medium text-ink-900">{l.supplierName ?? '—'}</span>
                    {l.supplierGstin && <span className="block font-mono text-[11px] text-ink-400">{l.supplierGstin}</span>}
                    {!settled && l.supplierFiledOn && <span className="block text-[11px] text-ink-400">filed {l.supplierFiledOn}</span>}
                  </td>
                  <td className={`px-5 ${pad}`} title={settled ? l.note ?? undefined : undefined}>
                    <span className="text-ink-800">{l.invoiceNumber ?? '—'}</span>
                    <span className="ml-2 text-[11px] text-ink-400">{l.invoiceDate ? date(l.invoiceDate) : ''}</span>
                    {l.reverseCharge && <span className="ml-1.5 rounded bg-ink-100 px-1 text-[10px] font-semibold uppercase text-ink-500">RCM</span>}
                    {/^cdn/.test(l.section ?? '') && <span className="ml-1.5 rounded bg-ink-100 px-1 text-[10px] font-semibold uppercase text-ink-500">Note</span>}
                    {/* The heading says what the group means; a row only speaks
                        when it has something of its OWN to add. */}
                    {!settled && l.note && !STATUS_HINT[l.status] && <span className="mt-1 block max-w-md text-xs text-ink-500">{l.note}</span>}
                    <CopyInvoiceNoButton line={l} entityId={entityId} onFixed={onFixed} />
                    {!settled && <UseGstinButton line={l} entityId={entityId} onFixed={onFixed} />}
                    {l.status === 'missing_in_books' && (
                      <button
                        onClick={() => setOpenBill(l)}
                        title="See what the supplier filed, and record it"
                        className="mt-1.5 inline-flex items-center gap-1 rounded-lg border border-ink-300 bg-paper px-2.5 py-1 text-[11px] font-semibold text-ink-700 transition hover:bg-ink-50"
                      >
                        <Eye className="h-3 w-3" /> View / Record
                      </button>
                    )}
                  </td>
                  <td className={`px-5 ${pad} text-xs text-ink-600`}>
                    {l.voucherNumber || l.bookedOn ? (
                      <>
                        <span className="block text-ink-700">Voucher {l.voucherNumber ?? '—'}</span>
                        <span className="block text-ink-400">{date(l.bookedOn)}</span>
                        {!settled && l.booksRef && l.booksRef !== l.invoiceNumber && <span className="block text-ink-400">recorded as {l.booksRef}</span>}
                      </>
                    ) : <span className="text-ink-400">not booked</span>}
                  </td>
                  <td className={`px-5 ${pad} text-right text-sm font-medium`}>{money(l)}</td>
                </tr>
              ))}
            </tbody>
          );
        })}
      </table>
      {openBill && <Bill2bModal line={openBill} period={period} entityId={entityId} onRecorded={onFixed} onClose={() => setOpenBill(null)} />}
    </div>
  );
}

/**
 * Pull the 2B straight from the portal, using the same taxpayer session the
 * filing steps use. 2B is never filed — this is a read, and it replaces the
 * period exactly as a manual upload does.
 */
function Fetch2bButton({ period, entityId, onLoaded }: {
  period: string; entityId: string | null; onLoaded: () => void;
}) {
  const [busy, setBusy] = useState<'fetch' | 'generate' | null>(null);
  /* GSTN will not hand over a 2B it never built — GTR2B-002, "kindly generate
     2B ondemand" — and there is no second pass after the 14th. The portal's
     own answer is the IMS dashboard's "Compute GSTR-2B"; this is that call,
     offered only once GSTN has said that is what is missing. */
  const [needsGenerating, setNeedsGenerating] = useState(false);

  const fetchIt = async () => {
    setBusy('fetch');
    try {
      const res = await adminFetch(`/api/gst/2b/fetch?period=${encodeURIComponent(period)}`, {
        method: 'POST', query: { entityId: entityId ?? undefined },
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) {
        // A missing session is not an error to report and forget: it is the
        // one step the user has to do, so say where that step is.
        if (out.needsSession) {
          throw new Error('No portal session — open one at the top of this screen, then fetch again.');
        }
        if (out.code === 'GTR2B-002' || /generate 2B ondemand|not generated by system/i.test(String(out.error ?? ''))) {
          setNeedsGenerating(true);
          throw new Error('GSTN has not generated this 2B yet. Generate it below, then fetch again.');
        }
        throw new Error(out.error || out.message || `Could not fetch the 2B (HTTP ${res.status})`);
      }
      setNeedsGenerating(false);
      // A nil 2B is a real answer — say what it means, not "0 document(s)".
      if (out.empty) toast.success(out.note ?? `GSTN holds no documents in the ${fmtPeriod(period)} 2B — no input credit to claim.`);
      else toast.success(`${out.stored ?? 0} document(s) fetched for ${fmtPeriod(period)}`);
      onLoaded();
    } catch (e) {
      toast.error((e as Error).message);
    } finally { setBusy(null); }
  };

  const generate = async () => {
    setBusy('generate');
    try {
      const res = await adminFetch(`/api/gst/2b/regenerate?period=${encodeURIComponent(period)}`, {
        method: 'POST', query: { entityId: entityId ?? undefined },
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (out.needsSession) throw new Error('No portal session — open one at the top of this screen first.');
        throw new Error(out.error || out.message || `GSTN would not generate it (HTTP ${res.status})`);
      }
      setNeedsGenerating(false);
      if (out.empty) { toast.success(out.note ?? 'GSTN holds no records in this 2B.'); onLoaded(); return; }
      toast.success('GSTN has built the 2B. Fetching it now.');
      setBusy('fetch');
      await fetchIt();
    } catch (e) {
      toast.error((e as Error).message);
    } finally { setBusy(null); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button onClick={fetchIt} disabled={!!busy}
        className="inline-flex items-center gap-2 rounded-full border border-ink-300 bg-paper px-5 py-2.5 text-sm font-semibold text-ink-700 transition hover:bg-ink-50 disabled:opacity-50">
        {busy === 'fetch' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        {busy === 'fetch' ? 'Fetching…' : 'Fetch from the portal'}
      </button>
      {needsGenerating && (
        <button onClick={generate} disabled={!!busy}
          className="inline-flex items-center gap-2 rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-paper transition hover:-translate-y-0.5 disabled:opacity-50">
          {busy === 'generate' && <Loader2 className="h-4 w-4 animate-spin" />}
          Generate it at GSTN
        </button>
      )}
    </div>
  );
}

function Recon2bView({ r, period, entityId, onLoaded }: { r: Recon; period: string; entityId: string | null; onLoaded: () => void }) {
  const s = r.summary;
  // The single-bill record dialog, opened from the not-in-books list.
  const [openBill, setOpenBill] = useState<ReconLine | null>(null);
  // The reconciliation, or the whole 2B as the portal's Excel shows it.
  const [mode, setMode] = useState<'recon' | 'portal'>('recon');
  /* The portal session is needed to FETCH a 2B or to file — not to work with
     one already loaded. Shown standing open it read as a required step, and
     people asked whether the reconciliation depended on it. It appears when
     asked for, or when a fetch says that is what is missing. */

  if (!r.hasPortalData) {
    return (
      <div className="rounded-2xl border border-dashed border-ink-300 bg-ink-50/50 px-6 py-12 text-center">
        <p className="text-sm font-medium text-ink-700">No GSTR-2B loaded for {fmtPeriod(period)}.</p>
        <p className="mx-auto mt-2 max-w-lg text-sm text-ink-600">
          2B is generated by GSTN from what your suppliers filed — it is the ceiling on the input credit this
          month&rsquo;s GSTR-3B may claim. Download it from the portal and load it here; the reconciliation and
          the 3B&rsquo;s Table 4 both follow from it.
        </p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          <Load2bButton period={period} entityId={entityId} onLoaded={onLoaded} />
          <Fetch2bButton period={period} entityId={entityId} onLoaded={onLoaded} />
        </div>
        <p className="mx-auto mt-3 max-w-lg text-xs text-ink-500">
          Or take the JSON by hand: Returns Dashboard &rsaquo; {fmtPeriod(period)} &rsaquo; GSTR-2B &rsaquo; Download &rsaquo; Generate JSON.
        </p>
        <div className="mx-auto mt-4 max-w-lg">
          <Gstr2bWorkbookView period={period} entityId={entityId} onChanged={onLoaded} />
        </div>
        <p className="mx-auto mt-3 flex max-w-lg flex-wrap items-center justify-center gap-2 text-xs text-ink-500">
          Fetching needs a portal session. Uploading the JSON does not.
          <button onClick={focusPortalSession} className="inline-flex items-center gap-1 rounded-lg border border-ink-300 bg-paper px-2 py-0.5 text-[11px] font-semibold text-ink-700 hover:bg-ink-50">
            <ArrowUp className="h-3 w-3" /> Open a session
          </button>
        </p>
      </div>
    );
  }

  const c = s.counts;
  /* Bills in 2B but not in the books that take a decision get their own list,
     with search, pages and bulk selection. Everything else to chase stays in
     the table as before. */
  const decidable = r.lines.filter((l) => l.status === 'missing_in_books' && (l.needsAction || l.decision));
  const action = r.lines.filter((l) => NEEDS_ACTION.includes(l.status) && !decidable.includes(l));
  const matched = r.lines.filter((l) => !NEEDS_ACTION.includes(l.status));

  /* Each problem, worst first, with what it costs and what to do. Ordered by
     money at stake: credit you cannot take, then credit the books cannot back. */
  const issues: { title: string; detail: string; fix?: { label: string; to: string } }[] = [];
  if (c.missingIn2b) issues.push({
    title: `${c.missingIn2b} bill${c.missingIn2b > 1 ? 's' : ''} your suppliers have not declared — ₹${inr(sumOf(r.lines, 'missing_in_2b'))}`,
    detail: 'The credit is not claimable until they file. Chase them; if they file next month it matches then, as "filed late", and nothing is lost.',
  });
  if (c.noGstin) issues.push({
    // Counted both ways on purpose: the money follows the bills, but the work
    // is one GSTIN per supplier. Saying "8 suppliers" when three ledgers are
    // missing a GSTIN made a ten-minute fix look like an afternoon's.
    title: `${c.noGstin} bill${c.noGstin > 1 ? 's' : ''} from ${supplierCount(r.lines, 'no_gstin')} supplier${supplierCount(r.lines, 'no_gstin') > 1 ? 's' : ''} charging GST with no GSTIN on the ledger — ₹${inr(sumOf(r.lines, 'no_gstin'))}`,
    detail: `Only a registered supplier can charge GST, so this is a gap in the ledger rather than an unregistered supplier. Where 2B holds the same bill, the line below names that GSTIN — “Use this GSTIN” saves it to the party and the page re-matches straight away${c.awaitingGstin ? `, releasing ₹${inr(s.reversedAwaitingGstin)} of reversed credit` : ''}. No need to fetch the 2B again; nothing at the portal has changed.`,
    fix: { label: 'Fix in Masters', to: '/admin/masters?tab=ledgers' },
  });
  if (c.itcBlocked) issues.push({
    title: `GSTN denies ₹${inr(s.blocked)} of credit on ${c.itcBlocked} document${c.itcBlocked > 1 ? 's' : ''}`,
    detail: 'Reported in 4D(2) and never claimed. This one is a cost, not a fix — the reason is on the line below.',
  });
  if (c.mismatch) issues.push({
    title: `${c.mismatch} document${c.mismatch > 1 ? 's' : ''} where the books and 2B disagree`,
    detail: 'Only the lower of the two figures is claimed. Ask the supplier to amend their GSTR-1 if theirs is the wrong one.',
  });
  if (c.missingInBooks) issues.push({
    title: `${c.missingInBooks} document${c.missingInBooks > 1 ? 's' : ''} in 2B that the books do not have${s.needsAction?.count ? ` — ${s.needsAction.count} still to decide` : ''}`,
    detail: `Nothing is held back on its own. For each, record it in the books (preferred), hold it back in 4B(2), or claim it anyway.${s.heldBack ? ` Held back so far: ₹${inr(s.heldBack)}.` : ''}${s.claimedWithoutBooks ? ` Claimed without a book entry: ₹${inr(s.claimedWithoutBooks)}.` : ''}`,
  });
  if (c.probable) issues.push({
    title: `${c.probable} matched on amount because the invoice numbers differ`,
    detail: 'The credit counts. Use the button on the line to put 2B\u2019s number on the voucher, so next month matches on the number instead of inferring it from the date and amount.',
  });

  const switcher = (
    <div role="tablist" aria-label="2B view" className="inline-flex rounded-full border border-ink-200 bg-paper p-1 text-xs font-semibold">
      <button role="tab" aria-selected={mode === 'recon'} onClick={() => setMode('recon')} className={`rounded-full px-3 py-1.5 ${mode === 'recon' ? 'bg-inkblack text-white' : 'text-ink-600'}`}>Reconciliation</button>
      <button role="tab" aria-selected={mode === 'portal'} onClick={() => setMode('portal')} className={`rounded-full px-3 py-1.5 ${mode === 'portal' ? 'bg-inkblack text-white' : 'text-ink-600'}`}>Full GSTR-2B (as on the portal)</button>
    </div>
  );
  if (mode === 'portal') {
    return (
      <div className="space-y-4">
        {switcher}
        <Gstr2bWorkbookView period={period} entityId={entityId} onChanged={onLoaded} onRecord={(l) => setOpenBill(l as ReconLine)} />
        {openBill && <Bill2bModal line={openBill} period={period} entityId={entityId} onRecorded={onLoaded} onClose={() => setOpenBill(null)} />}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {switcher}
      {!!s.needsAction?.count && (
        <NotInBooksBanner count={s.needsAction.count} tax={s.needsAction.tax} period={period} entityId={entityId} onDone={onLoaded} />
      )}
      {/* ── 1 · the credit ─────────────────────────────────────────────── */}
      <section className="space-y-4">
        <Band n={1} title="The credit 2B allows" hint={`2B for ${fmtPeriod(r.period)}${r.loadedAt ? `, loaded ${new Date(r.loadedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}` : ''} · books claim ₹${inr(s.booksTotal)}, 2B lists ₹${inr(s.portalTotal)}`} />

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Tile label="ITC claimable (4C)" value={`₹${inr(s.claimable)}`} sub={`${c.matched + c.probable + c.matchedNoRef + c.matchedLate} matched · after reversals`} tone="ok" />
          <Tile label="ITC at risk" value={`₹${inr(s.atRisk)}`} sub={c.missingIn2b + c.noGstin ? `${c.missingIn2b} not in 2B · ${c.noGstin} no GSTIN` : 'every bill is in 2B'} tone={s.atRisk > 0 ? 'warn' : undefined} />
          <Tile
            label="Reversed, 4B(2)"
            value={`₹${inr(s.reversed)}`}
            // Two different problems with two different fixes: book the bill,
            // or fill in one field. One number over both read as neither.
            sub={[
              s.heldBack ? `held back by you ₹${inr(s.heldBack)}` : '',
              c.awaitingGstin ? `${c.awaitingGstin} awaiting GSTIN ₹${inr(s.reversedAwaitingGstin)}` : '',
              s.needsAction?.count ? `${s.needsAction.count} undecided — not reversed` : '',
            ].filter(Boolean).join(' · ') || 'nothing to reverse'}
            tone={s.reversed > 0 ? 'warn' : undefined}
          />
          <Tile label="Blocked, 4D(2)" value={`₹${inr(s.blocked)}`} sub={`${c.itcBlocked} denied by GSTN`} tone={s.blocked > 0 ? 'warn' : undefined} />
        </div>

        <Itc3bPanel r={r} />

        {r.otherCredits.length > 0 && (
          <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-paper">
            <div className="border-b border-ink-100 px-5 py-3">
              <p className="text-sm font-semibold text-ink-900">Imports and ISD credit</p>
              <p className="text-xs text-ink-500">From ICEGATE and Input Service Distributors — no supplier invoice to match; they go straight to 4A(1) and 4A(4).</p>
            </div>
            <table className="w-full min-w-[40rem] text-sm">
              <tbody className="divide-y divide-ink-50">
                {r.otherCredits.map((o, i) => (
                  <tr key={i} className="text-ink-700">
                    <td className="px-5 py-2 text-xs font-semibold uppercase text-ink-500">{/^impg/.test(o.section) ? 'Import · 4A(1)' : 'ISD · 4A(4)'}</td>
                    <td className="px-5 py-2"><span className="font-medium text-ink-900">{o.invoiceNumber}</span><span className="block text-[11px] text-ink-400">{o.invoiceDate ?? ''}</span></td>
                    <td className="px-5 py-2">{o.supplierName ?? '—'}{o.supplierGstin && <span className="block font-mono text-[11px] text-ink-400">{o.supplierGstin}</span>}</td>
                    <td className="px-5 py-2 text-right tabular-nums">{inr(o.portal.tax)}{!o.itcAvailable && <span className="block text-[11px] text-danger-700">not eligible</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── 2 · what to chase ──────────────────────────────────────────── */}
      <section className="space-y-4">
        <Band
          n={2}
          tone={action.length ? 'danger' : 'ok'}
          title={action.length ? `Chase before you file — ${action.length}` : 'Nothing to chase'}
          hint={action.length ? 'Each one changes what this month can claim.' : 'Every document in 2B is in the books, and every bill is in 2B.'}
        />
        {issues.length === 0 ? (
          <div className="flex items-center gap-2 rounded-2xl border border-ok-200 bg-ok-50/60 px-5 py-4 text-sm text-ok-700">
            <CheckCircle2 className="h-4 w-4 shrink-0" /> The books and 2B agree.
          </div>
        ) : (
          <>
            <div className="divide-y divide-ink-100 overflow-hidden rounded-2xl border border-danger-200 bg-paper">
              {issues.map((b) => (
                <div key={b.title} className="flex items-start gap-3 px-5 py-4">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-danger-600" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-ink-900">{b.title}</p>
                    <p className="mt-0.5 text-sm text-ink-600">{b.detail}</p>
                  </div>
                  {b.fix && (
                    <Link to={b.fix.to} className="shrink-0 rounded-full border border-ink-300 bg-paper px-3 py-1.5 text-xs font-semibold text-ink-700 no-underline hover:bg-ink-50">
                      {b.fix.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>
            {action.length > 0 && <ReconTable lines={action} entityId={entityId} period={period} onFixed={onLoaded} />}
            {decidable.length > 0 && (
              <div className="space-y-2">
                <div>
                  <p className="text-sm font-semibold text-ink-900">In 2B, not in the books — {decidable.length.toLocaleString('en-IN')}</p>
                  <p className="text-xs text-ink-500">Tick bills to record, hold back or claim them together. Nothing is chosen for you.</p>
                </div>
                <NotInBooksList lines={decidable} period={period} entityId={entityId} onChanged={onLoaded} onRecord={(l) => setOpenBill(l as ReconLine)} />
              </div>
            )}
          </>
        )}
      </section>

      {openBill && <Bill2bModal line={openBill} period={period} entityId={entityId} onRecorded={onLoaded} onClose={() => setOpenBill(null)} />}
      {/* ── 3 · settled ────────────────────────────────────────────────── */}
      <section className="space-y-4">
        <Band
          n={3}
          title={`Matched — ${matched.length}`}
          // "Nothing to do" was not quite true once the rows could be tidied,
          // and "re-load the 2B" read as though a ledger edit needed a portal call.
          hint={c.matchedNoRef
            ? `Nothing here blocks filing. ${c.matchedNoRef} matched on date and amount — copy the invoice number from 2B on those rows and next month matches on the number. Fetch the 2B again only if suppliers have filed since.`
            : 'Nothing to do with these. Fetch the 2B again if suppliers have filed since.'}
          tone="ok"
        />
        {matched.length > 0 ? <ReconTable lines={matched} entityId={entityId} period={period} onFixed={onLoaded} settled /> : (
          <p className="rounded-2xl border border-ink-200 bg-paper px-5 py-4 text-sm text-ink-500">Nothing has matched yet.</p>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-ink-200 bg-paper px-5 py-4">
          <div>
            <p className="text-sm font-semibold text-ink-900">Re-load the 2B</p>
            <p className="mt-0.5 text-xs text-ink-500">GSTN regenerates it as suppliers file. Either route replaces this period rather than adding to it.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Fetch2bButton period={period} entityId={entityId} onLoaded={onLoaded} />
            <Load2bButton period={period} entityId={entityId} onLoaded={onLoaded} label="Re-load 2B" />
          </div>
        </div>

        {/* Every number recorded from this screen or from Corrections, across
            every period — closed by default, because it is a record to audit
            rather than a step in filing. */}
        <InvoiceNumbersPanel entityId={entityId} />
      </section>
    </div>
  );
}

/** Books-side tax of every line with a given status. */
function sumOf(lines: ReconLine[], status: MatchStatus): number {
  return Math.round(lines.filter((l) => l.status === status).reduce((t, l) => t + (l.books?.tax ?? 0), 0) * 100) / 100;
}

/** Distinct suppliers behind the lines with a given status. Several bills from
 *  one supplier are one thing to fix, not several. */
function supplierCount(lines: ReconLine[], status: MatchStatus): number {
  const names = new Set<string>();
  for (const l of lines) {
    if (l.status !== status) continue;
    names.add((l.supplierGstin ?? l.supplierName ?? l.voucherId ?? '').trim().toUpperCase());
  }
  return names.size;
}

/* ── Register view ──────────────────────────────────────────────────────── */

function RegisterView({ view, data, rows, onlyIssues, setOnlyIssues, entityId, onChanged }: { view: Direction; data: RegisterResp; rows: GstDoc[]; onlyIssues: boolean; setOnlyIssues: (b: boolean) => void; entityId: string | null; onChanged: () => void }) {
  const s = data.summary;
  if (s.count === 0) return <Empty label={view === 'outward' ? 'sales' : 'purchase'} />;
  return (
    <div className="space-y-6">
      {!data.companyGstin && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-warn-200 bg-warn-50/60 px-4 py-3 text-sm text-warn-700">
          <ShieldAlert className="h-5 w-5 shrink-0 text-warn-600" />
          <span className="flex-1">This entity has no GSTIN set — needed on the return.</span>
          <Link to="/admin/masters" className="rounded-full bg-paper px-3 py-1.5 text-xs font-semibold text-warn-700 no-underline ring-1 ring-warn-300 hover:bg-warn-100">Set it in Masters → Company</Link>
        </div>
      )}
      <ValidationBar direction={view} entityId={entityId} changes={data.changes} onSaved={onChanged} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Taxable value" value={`₹${inr(s.taxableValue)}`} sub={`${s.count} documents`} />
        <Tile label="Total tax" value={`₹${inr(s.totalTax)}`} sub={`CGST ${inr(s.cgst)} · SGST ${inr(s.sgst)} · IGST ${inr(s.igst)}`} />
        <Tile label="Invoice value" value={`₹${inr(s.invoiceValue)}`} sub="Taxable + tax" />
        <Tile label="Needs review" value={String(s.withIssues)} sub={s.withIssues ? 'documents with issues' : 'all documents valid'} tone={s.withIssues ? 'warn' : 'ok'} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Breakdown title="By tax rate" rows={s.byRate.map((r) => ({ label: r.rate == null ? 'Mixed — needs line split' : `${r.rate}%`, count: r.count, taxable: r.taxableValue, tax: r.tax }))} />
        <Breakdown title="By supply type" rows={s.bySupplyType.map((t) => ({ label: t.supplyType, count: t.count, taxable: t.taxableValue, tax: t.tax }))} />
      </div>

      {data.missingInfo.length > 0 && <MissingInfo rows={data.missingInfo} onPick={() => setOnlyIssues(true)} />}

      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-ink-400">{view === 'outward' ? 'Sales' : 'Purchase'} register</h2>
          <div className="flex items-center gap-4">
            <Link to="/admin/masters" className="text-xs font-medium text-inkblack no-underline hover:text-inkblack">Fix in Masters ↗</Link>
            {s.withIssues > 0 && (
              <label className="inline-flex items-center gap-2 text-xs font-medium text-ink-600">
                <input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} className="rounded border-ink-300" />
                Only show {s.withIssues} needing review
              </label>
            )}
          </div>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-paper">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-400">
                <th className="px-4 py-3 font-medium">Date</th><th className="px-4 py-3 font-medium">{view === 'inward' ? 'Supplier inv.' : 'No.'}</th><th className="px-4 py-3 font-medium">Party</th>
                <th className="px-4 py-3 font-medium">GSTIN</th><th className="px-4 py-3 text-center font-medium">POS</th>
                <th className="px-4 py-3 text-right font-medium">Taxable</th><th className="px-4 py-3 text-right font-medium">CGST</th>
                <th className="px-4 py-3 text-right font-medium">SGST</th><th className="px-4 py-3 text-right font-medium">IGST</th>
                <th className="px-4 py-3 text-center font-medium">Rate</th><th className="px-4 py-3 font-medium">Type</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.voucherId} className={`border-b border-ink-100 last:border-0 ${d.issues.length ? 'bg-warn-50/40' : ''}`}>
                  <td className="whitespace-nowrap px-4 py-2.5 text-ink-500">{d.date}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-ink-700">
                    {view === 'inward' ? (
                      /* A bill's identity is the SUPPLIER's number — what they file and
                         what 2B lists. Ours is shown under it, for finding the voucher. */
                      <>
                        {d.supplierInvoiceNo ?? <span className="text-warn-700" title="Supplier invoice no. not recorded — GSTR-2B matches on it">—</span>}
                        {d.supplierInvoiceNoSource === 'alias' && (
                          <span className="ml-1 rounded bg-ink-100 px-1 text-[10px] font-medium text-ink-500" title="Held here beside the voucher, not written onto it — the voucher stays exactly as Tally sent it">held here</span>
                        )}
                        <span className="block text-[11px] text-ink-400">ours {d.number ?? '—'}{d.type === 'Journal' ? ' · journal' : ''}</span>
                      </>
                    ) : (d.number ?? '—')}
                    {d.isNote && <span className="ml-1 rounded bg-ink-100 px-1 text-[10px] text-ink-500">{d.type.includes('Credit') ? 'CN' : 'DN'}</span>}
                    {d.reverseCharge && <span className="ml-1 rounded bg-ink-100 px-1 text-[10px] text-ink-500" title="Reverse charge — tax paid by you in 3.1(d), claimed in 4A(3)">RCM</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="text-ink-800">{d.partyName ?? '—'}</span>
                    {d.issues.length > 0 && <span className="ml-2 inline-flex items-center gap-1 align-middle text-[11px] font-medium text-warn-700" title={d.issues.map((i) => i.message).join('\n')}><AlertTriangle className="h-3 w-3" /> {d.issues.length}</span>}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs text-ink-500">{d.partyGstin ?? '—'}</td>
                  <td className="px-4 py-2.5 text-center text-ink-500">{d.placeOfSupply ?? '—'}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-ink-800">{inr(d.taxableValue)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-ink-500">{d.cgst ? inr(d.cgst) : '—'}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-ink-500">{d.sgst ? inr(d.sgst) : '—'}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-ink-500">{d.igst ? inr(d.igst) : '—'}</td>
                  <td className="px-4 py-2.5 text-center text-ink-500"><RateCell d={d} /></td>
                  <td className="px-4 py-2.5"><span className="rounded-full bg-ink-100 px-2 py-0.5 text-[11px] font-medium text-ink-600">{d.supplyType}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ── Validation snapshot bar (#7) ───────────────────────────────────────── */

function ValidationBar({ direction, entityId, changes, onSaved }: { direction: Direction; entityId: string | null; changes: Changes; onSaved: () => void }) {
  const [saving, setSaving] = useState(false);
  const drift = changes.added.length + changes.removed.length + changes.changed.length;
  const noEntity = !entityId;

  async function save() {
    setSaving(true);
    try {
      const res = await adminFetch('/api/gst/validate/save', { method: 'POST', query: { direction, entityId: entityId ?? undefined } });
      const b = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(b?.message || 'Failed');
      toast.success(`Validated — ${b.docCount} documents saved as baseline`); onSaved();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed to save'); }
    finally { setSaving(false); }
  }

  const tone = noEntity ? 'neutral' : !changes.hasBaseline ? 'neutral' : drift > 0 ? 'warn' : 'ok';
  const ring = tone === 'warn' ? 'border-warn-200 bg-warn-50/50' : tone === 'ok' ? 'border-ok-200 bg-ok-50/50' : 'border-ink-200 bg-paper';
  return (
    <div className={`flex flex-wrap items-center justify-between gap-3 rounded-2xl border p-4 ${ring}`}>
      <div className="text-sm">
        {noEntity ? (
          <span className="text-ink-500">Select a single entity to save and track a validated baseline.</span>
        ) : !changes.hasBaseline ? (
          <span className="text-ink-600">Not yet validated. Save a baseline to track changes on future imports.</span>
        ) : drift > 0 ? (
          <span className="font-medium text-warn-700">
            {changes.changed.length} changed · {changes.added.length} new · {changes.removed.length} removed
            <span className="font-normal text-warn-600"> since last validated {changes.baselineAt ? new Date(changes.baselineAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : ''}</span>
          </span>
        ) : (
          <span className="font-medium text-ok-700">No changes since last validated {changes.baselineAt ? new Date(changes.baselineAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : ''} · {changes.unchanged} documents</span>
        )}
      </div>
      {!noEntity && (
        <button onClick={save} disabled={saving}
          className="rounded-full border border-ink-300 bg-paper px-4 py-2 text-xs font-semibold text-ink-700 transition hover:bg-ink-50 disabled:opacity-50">
          {changes.hasBaseline ? 'Re-validate & save' : 'Save as validated'}
        </button>
      )}
    </div>
  );
}

/* ── Missing-Info report (#3) ───────────────────────────────────────────── */

function MissingInfo({ rows, onPick }: { rows: MissingInfoRow[]; onPick: () => void }) {
  const errors = rows.filter((r) => r.severity === 'error').reduce((s, r) => s + r.count, 0);
  const warns = rows.filter((r) => r.severity === 'warning').reduce((s, r) => s + r.count, 0);
  return (
    <div className="rounded-2xl border border-warn-200 bg-warn-50/50 p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="grid size-8 place-items-center rounded-lg bg-warn-100 text-warn-600"><AlertTriangle className="h-4 w-4" /></span>
          <div>
            <p className="text-sm font-semibold text-ink-900">Missing information</p>
            <p className="text-xs text-ink-500">{errors} error{errors !== 1 ? 's' : ''} · {warns} warning{warns !== 1 ? 's' : ''} to review before filing</p>
          </div>
        </div>
        <button onClick={onPick} className="rounded-full border border-ink-300 bg-paper px-3.5 py-1.5 text-xs font-semibold text-ink-700 transition hover:bg-ink-50">Review flagged rows</button>
      </div>
      <div className="divide-y divide-warn-100 rounded-xl border border-warn-100 bg-paper/70">
        {rows.map((r) => (
          <div key={r.code} className="flex items-center justify-between px-3.5 py-2.5">
            <span className="flex items-center gap-2 text-sm text-ink-700">
              <span className={`size-1.5 rounded-full ${r.severity === 'error' ? 'bg-danger-500' : 'bg-warn-400'}`} />
              {r.label}
            </span>
            <span className="text-xs font-semibold tabular-nums text-ink-500">{r.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Filing preparer & tracker (Phase 4) ───────────────────────────────── */

interface Readiness {
  gstin: string | null; gstinMissing: boolean; blockingErrors: number; warnings: number; canFile: boolean;
  issues?: { code: string; severity: 'error' | 'warning'; count: number; example: string; items?: { voucherNo: string | null; stockItemName: string | null; taxableValue: number }[] }[];
}
interface Filing { id: string; returnType: string; period: string; status: string; taxableValue: number; totalTax: number; blockingErrors: number; warnings: number; arn: string | null; createdAt: string; filedAt: string | null; source?: string; arnUnconfirmed?: boolean }

function FilingPanel({ returnType, entityId, part, portalHolds = false }: {
  returnType: 'GSTR1' | 'GSTR3B'; entityId: string | null; part: string;
  /** GSTN already holds a built, ready-to-sign return for this period. */
  portalHolds?: boolean;
}) {
  // The period the page is showing. Prepare used to omit it, so the filing
  // record was written with no month — an ARN that cannot say which return it
  // belongs to. The API now refuses that, so it has to be sent.
  const { from, to } = usePeriod();
  const [preparing, setPreparing] = useState(false);
  const [readiness, setReadiness] = useState<Readiness | null>(null);
  const [filingId, setFilingId] = useState<string | null>(null);
  const [arn, setArn] = useState('');
  const [arnFiledOn, setArnFiledOn] = useState('');
  const arnOk = /^[A-Z]{2}\d{13}$/.test(arn.replace(/\s+/g, ''));
  const [filings, setFilings] = useState<Filing[]>([]);
  /* What the portal last said about each return, and when it was asked. */
  const [portal, setPortal] = useState<PortalEntry[]>([]);

  const loadFilings = () => adminFetchJson<{ filings: Filing[]; portal?: PortalEntry[] }>('/api/gst/filings', { query: { entityId: entityId ?? undefined } }).then((d) => { setFilings(d.filings); setPortal(d.portal ?? []); }).catch(() => {});
  useEffect(() => { setReadiness(null); setFilingId(null); if (entityId) loadFilings(); }, [entityId, returnType, from, to]);
  // The filing panel below may learn from the portal that this return is
  // filed; it says so, and the history is read again to show it.
  useEffect(() => {
    const again = () => { if (entityId) loadFilings(); };
    window.addEventListener(GST_FILED_EVENT, again);
    return () => window.removeEventListener(GST_FILED_EVENT, again);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  async function prepare() {
    setPreparing(true);
    try {
      const res = await adminFetch('/api/gst/file/prepare', {
        method: 'POST',
        query: { type: returnType, entityId: entityId ?? undefined, from: from || undefined, to: to || undefined },
      });
      const b = await res.json().catch(() => ({}));
      // 422: not fit to file. Nothing was recorded, and the issues come back
      // to be shown in the same panel a successful check uses.
      if (res.status === 422 && b?.readiness) { setReadiness(b.readiness); setFilingId(null); return; }
      if (!res.ok || !b.ok) throw new Error(b?.message || b?.error || 'Failed');
      setReadiness(b.readiness); setFilingId(b.filingId); loadFilings();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed'); }
    finally { setPreparing(false); }
  }
  async function file() {
    if (!filingId || !arnOk) return;
    try {
      const res = await adminFetch(`/api/gst/file/${filingId}/mark-filed`, {
        method: 'POST', query: { entityId: entityId ?? undefined },
        body: JSON.stringify({ arn, filedOn: arnFiledOn || undefined }),
      });
      const b = await res.json().catch(() => ({}));
      if (!res.ok || !b.ok) throw new Error(b?.error || b?.message || 'Failed');
      /* Said either way: a record the portal agreed is a different thing from
         one that was taken on the person's word. */
      if (b.confirmed) toast.success('Recorded — the portal confirms this ARN');
      else toast.warning ? toast.warning(b.note ?? 'Recorded, but not checked against the portal.') : toast.success(b.note ?? 'Recorded, but not checked against the portal.');
      setReadiness(null); setFilingId(null); setArn(''); setArnFiledOn(''); loadFilings();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed'); }
  }

  const mine = filings.filter((f) => f.returnType === returnType);
  /* The month being looked at, as a return names it (MMYYYY). A period the
     history has as filed — here, or by the portal's own word — is closed:
     preparing it again would only make a second, meaningless record. */
  const toDate = to ? new Date(to) : null;
  const periodKey = toDate && !isNaN(toDate.getTime()) ? `${String(toDate.getMonth() + 1).padStart(2, '0')}${toDate.getFullYear()}` : '';
  const filedHere = mine.find((f) => f.period === periodKey && isFiled(f)) ?? null;
  /* No escape hatch of its own: while GSTN is holding the return, checking it
     again answers a question nobody is asking. It comes back when the portal
     stops holding it — cleared there, or declared out of date by the upload
     below, which is the same decision said once. */
  const locked = portalHolds && !readiness;
  return (
    <div className="rounded-2xl border border-ink-200 bg-paper p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-ink-900">
            <span className="mr-1.5 font-mono text-[11px] text-ink-400">{part}</span>
            Check the return
          </p>
          <p className="mt-0.5 text-xs text-ink-500">
            {locked
              ? 'GSTN already holds this return, checked and ready to sign. Re-check only if the books have changed since.'
              : `Builds the ${returnType === 'GSTR3B' ? 'GSTR-3B' : 'GSTR-1'} and runs GSTN’s own validations over it. Nothing is sent to the portal.`}
          </p>
        </div>
        {!readiness && (filedHere ? (
          <button disabled title="The portal reports this return filed for this period" className="inline-flex items-center gap-1.5 rounded-full bg-ink-100 px-4 py-2 text-xs font-semibold text-ink-500">
            Already filed{filedHere.arn ? ` — ARN ${filedHere.arn}` : ''}
          </button>
        ) : locked ? (
          /* Locked, not hidden. Building the return again is harmless — the
             upload below rebuilds its own payload from the books and never
             reads this record — but a prominent "Prepare for filing" beside a
             return GSTN is already holding reads as the next thing to press,
             and the next thing to press is the signature. */
          <button disabled title="The portal already holds this return" className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-ink-100 px-4 py-2 text-xs font-semibold text-ink-500">
            <Check className="h-3.5 w-3.5 text-ok-600" /> Checked
          </button>
        ) : (
          <button onClick={prepare} disabled={preparing || !entityId} className="inline-flex items-center gap-1.5 rounded-full bg-inkblack px-4 py-2 text-xs font-semibold text-white transition hover:-translate-y-0.5 disabled:opacity-50">{preparing ? 'Checking…' : 'Prepare for filing'}</button>
        ))}
      </div>

      {readiness && (
        <div className="mt-4 space-y-3">
          <div className={`rounded-xl border p-3.5 ${readiness.canFile ? 'border-ok-200 bg-ok-50/60' : 'border-danger-200 bg-danger-50/60'}`}>
            <p className={`text-sm font-medium ${readiness.canFile ? 'text-ok-700' : 'text-danger-700'}`}>
              {readiness.canFile ? 'Ready to file.' : `${readiness.blockingErrors} blocking issue${readiness.blockingErrors !== 1 ? 's' : ''} — fix before filing.`}
            </p>
            <p className="mt-1 text-xs text-ink-600">
              {readiness.gstinMissing ? 'Entity GSTIN not set. ' : `GSTIN ${readiness.gstin}. `}
              {readiness.warnings > 0 && `${readiness.warnings} warning(s) to review. `}
            </p>
            {/* A count on its own cannot be acted on. Name each one, how many
                documents carry it, and one real example to recognise it by. */}
            {!!readiness.issues?.length && (
              <ul className="mt-2.5 space-y-1.5 border-t border-danger-200/70 pt-2.5">
                {readiness.issues.filter((i) => i.severity === 'error').map((i) => (
                  <li key={i.code} className="text-xs">
                    <span className="font-semibold text-danger-800">{i.count}×</span>{' '}
                    <span className="font-mono text-[11px] text-ink-500">{i.code}</span>
                    <span className="block text-ink-600">{i.example}</span>
                    {!!i.items?.length && (
                      <ul className="mt-1 max-h-48 space-y-0.5 overflow-y-auto text-ink-600">
                        {i.items.map((m, n) => (
                          <li key={n} className="flex justify-between gap-3 tabular-nums">
                            <span>{m.voucherNo ?? '—'} · {m.stockItemName ?? 'ledger value'}</span>
                            <span>₹{inr(m.taxableValue)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {/* Filing happens below, from this app. This stays for the other
              case, which is common and used to be the only one: the client,
              or their own accountant, filed it on the portal directly, and
              the ARN has to be recorded here or oddits keeps asking. */}
          {readiness.canFile && (
            <>
              <p className="text-xs text-ink-500">Ready. Sign and send it from the next card. Already filed on the portal directly? Record the ARN here instead. It is checked against the portal when a session is open; otherwise it is kept as unconfirmed:</p>
              <div className="flex flex-wrap items-center gap-2">
                <input value={arn} onChange={(e) => setArn(e.target.value.toUpperCase())} placeholder="ARN from portal" className="w-56 rounded-xl border border-ink-200 bg-paper px-3 py-2 font-mono text-sm text-ink-900 focus:border-ink-300 focus-visible:outline-none" />
                <input type="date" value={arnFiledOn} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setArnFiledOn(e.target.value)} aria-label="Date filed on the portal" title="Date filed, as the portal shows it. The portal's own date is used when a session is open." className="rounded-xl border border-ink-200 bg-paper px-3 py-2 text-sm text-ink-900 focus-visible:outline-none" />
                <button onClick={file} disabled={!arnOk} title={arnOk ? undefined : 'An ARN is two letters then thirteen digits, e.g. AA3609266847594'} className="rounded-full border border-ink-300 bg-paper px-4 py-2 text-xs font-semibold text-ink-700 transition hover:bg-ink-50 disabled:opacity-40">Record as filed</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* When the portal was last asked about this month's return, and a warning
          if what oddits has recorded and what the portal says do not agree. */}
      {periodKey && (
        <div className="mt-4">
          <PortalTrustNote rows={mine.filter((f) => f.period === periodKey)} entry={portal.find((p) => p.returnType === returnType && p.period === periodKey)} />
        </div>
      )}

      {mine.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">Filing history</p>
          <div className="divide-y divide-ink-100 rounded-xl border border-ink-200">
            {mine.slice(0, 6).map((f) => (
              <div key={f.id} className="flex flex-wrap items-center justify-between px-3.5 py-2.5 text-sm">
                <span className="text-ink-600">{f.period || '—'} · <span className="tabular-nums">₹{inr(f.taxableValue)}</span></span>
                <span className="flex items-center gap-2">
                  {f.arn && <span className="font-mono text-[11px] text-ink-400">{f.arn}</span>}
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${isFiled(f) ? 'bg-ok-50 text-ok-600' : filedClaimed(f) ? 'bg-warn-100 text-warn-700' : 'bg-ink-100 text-ink-500'}`}>{isFiled(f) && f.source === 'portal' ? 'filed (outside oddits)' : statusLabel(f)}</span>
                </span>
                {filedWithoutArn(f) && (
                  <div className="flex w-full flex-wrap items-center justify-between gap-2 pt-1.5">
                    <span className="text-[11px] text-warn-700">Marked filed without an ARN. Ask the portal; if it says not filed this can go back to prepared.</span>
                    <FiledClaimActions filingId={f.id} entityId={entityId} onDone={loadFilings} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The cash and credit ledgers, beside the liability they pay.
 *
 * A 3B is settled from the credit ledger first, under the utilisation rules,
 * and from cash for the rest. The rules and the order are GSTN's — this does
 * not compute the set-off, it shows what is there to set off WITH, which is
 * the thing a CA leaves for the portal to find out.
 */
/**
 * Table 5.1, read from GSTN.
 *
 * Interest under rule 88B runs to the date of payment and the late fee is per
 * day per Act, so both are GSTN's computation. Asking for them is read-only —
 * nothing is saved, nothing is offset — and what comes back is reported as
 * GSTN sent it, including a zero it actually computed. A shape we cannot read
 * says so and points at the submission log rather than showing a zero nobody
 * worked out.
 */
function Gstn51({ period, entityId, live, liab, setLiab }: {
  period: string; entityId: string | null; live: boolean;
  liab: any; setLiab: (v: any) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const read = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await adminFetch('/api/gst/gstr3b/liability', { method: 'POST', query: { entityId: entityId ?? undefined, period } });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (out.needsSession) throw new Error('No portal session — open one at the top of this screen, then read 5.1.');
        throw new Error(out.error || `Could not read GSTN's figures (HTTP ${res.status})`);
      }
      setLiab(out);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-ink-200 bg-paper px-5 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink-900">GSTN&rsquo;s interest and late fee</p>
          <p className="mt-0.5 text-xs text-ink-500">
            {liab?.readAt
              ? `Read ${new Date(liab.readAt).toLocaleString('en-IN')}${liab.source ? ` from ${liab.source}` : ''}. These fill 5.1 above.`
              : 'Not read yet. 5.1 above stays blank until GSTN says what it has computed.'}
          </p>
        </div>
        <button onClick={read} disabled={busy || !live}
          title={live ? undefined : 'Needs an open portal session'}
          className="inline-flex shrink-0 items-center gap-2 rounded-full border border-ink-300 bg-paper px-4 py-2 text-xs font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {busy ? 'Asking GSTN…' : liab ? 'Read again' : 'Read from the portal'}
        </button>
      </div>

      {err && <p className="mt-3 rounded-xl border border-danger-200 bg-danger-50 px-3.5 py-2.5 text-xs text-danger-700">{err}</p>}

      {liab && (
        <p className="mt-3 text-xs text-ink-600">
          Interest <span className="font-semibold tabular-nums">₹{inr(liab.interest?.total ?? 0)}</span>
          {' · '}late fee <span className="font-semibold tabular-nums">₹{inr(liab.lateFee?.total ?? 0)}</span>
          {liab.negativeLiability?.total ? <> · negative liability carried forward <span className="font-semibold tabular-nums">₹{inr(liab.negativeLiability.total)}</span></> : null}
          . Both are paid in cash, from the head they belong to.
        </p>
      )}
    </div>
  );
}


/**
 * Table 8 — nil-rated, exempted and non-GST — document by document.
 *
 * GSTN takes four fixed supply types, each with all three amounts. Which row
 * a sale belongs in is decided by two pieces of master data: the ledger's
 * taxability (nil / exempt / non-GST) and the place of supply. Either can be
 * missing, and a missing one is not ours to decide — putting non-GST turnover
 * in the exempt column misstates the return even though the totals agree.
 *
 * So each document is listed with a tick or a cross, and the crosses say what
 * to set. Typed once on the ledger, every later month pairs on it.
 */
/**
 * State a ledger's taxability from the list that shows it is missing.
 *
 * The ✓/✗ list could name the gap but not close it, and the instruction it
 * gave — "set these on the ledger" — pointed at a Masters screen that has no
 * field for it. One sale's answer settles every sale through that ledger, this
 * month and every month after, which is why it is set here and not per
 * document.
 */
function TaxabilitySelect({ ledgerId, ledgerName, value, entityId, onSaved }: {
  ledgerId: string | null; ledgerName: string | null; value: string | null;
  entityId: string | null; onSaved: () => void;
}) {
  const [busy, setBusy] = useState(false);
  if (!ledgerId) return <span className="text-danger-700">not set</span>;

  const save = async (v: string) => {
    setBusy(true);
    try {
      await adminFetchJson(`/api/gst/ledger/${ledgerId}/taxability`, {
        method: 'PUT', query: { entityId: entityId ?? undefined },
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ taxability: v }),
      });
      toast.success(v
        ? `${ledgerName ?? 'Ledger'} is ${v === 'nongst' ? 'non-GST' : v === 'nil' ? 'nil-rated' : 'exempt'}`
        : `${ledgerName ?? 'Ledger'} taxability cleared`);
      onSaved();
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <select
      value={value ?? ''} disabled={busy} onChange={(e) => save(e.target.value)}
      title={ledgerName ? `Sets it on the ledger "${ledgerName}" — every sale through it, every month` : undefined}
      className={`rounded-lg border px-2 py-1 text-xs ${value
        ? 'border-ink-200 bg-paper text-ink-800'
        : 'border-danger-300 bg-danger-50 text-danger-800'}`}
    >
      <option value="">not set</option>
      <option value="nil">Nil-rated</option>
      <option value="exempt">Exempt</option>
      <option value="nongst">Non-GST</option>
    </select>
  );
}

function NilSupplies({ nil, entityId, onChanged }: { nil: any; entityId: string | null; onChanged: () => void }) {
  if (!nil?.documents?.length) return null;
  const unmapped = nil.documents.filter((d: any) => !d.mapped);
  const Tick = ({ ok }: { ok: boolean }) => ok
    ? <Check className="h-3.5 w-3.5 text-ok-600" aria-label="mapped" />
    : <X className="h-3.5 w-3.5 text-danger-600" aria-label="not mapped" />;

  return (
    <div className="overflow-hidden rounded-2xl border border-ink-200 bg-paper">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-ink-100 px-5 py-3">
        <div>
          <p className="text-sm font-semibold text-ink-900">Table 8 — nil-rated, exempt and non-GST</p>
          <p className="mt-0.5 text-xs text-ink-500">
            GSTN files these as four supply types, not one total. Which row a sale lands in comes from the ledger&rsquo;s taxability and the place of supply.
          </p>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${unmapped.length ? 'bg-danger-50 text-danger-700' : 'bg-ok-50 text-ok-700'}`}>
          {unmapped.length ? `${unmapped.length} of ${nil.documents.length} not mapped` : `All ${nil.documents.length} mapped`}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-ink-100 text-left text-[11px] uppercase tracking-wide text-ink-400">
              <th className="px-5 py-2 font-medium">Document</th>
              <th className="px-3 py-2 font-medium">Party</th>
              <th className="px-3 py-2 text-right font-medium">Taxable</th>
              <th className="px-3 py-2 text-center font-medium">Taxability</th>
              <th className="px-3 py-2 text-center font-medium">Place of supply</th>
              <th className="px-3 py-2 font-medium">Files as</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-50">
            {nil.documents.map((d: any, i: number) => (
              <tr key={`${d.number}-${i}`} className={d.mapped ? '' : 'bg-danger-50/40'}>
                <td className="px-5 py-2 text-ink-800">{d.number || '—'}<span className="ml-2 text-[11px] text-ink-400">{d.date}</span></td>
                <td className="px-3 py-2 text-ink-700">{d.party || <span className="text-ink-400">unregistered</span>}</td>
                <td className="px-3 py-2 text-right tabular-nums text-ink-800">₹{inr(d.taxableValue)}</td>
                <td className="px-3 py-2">
                  <span className="flex items-center justify-center gap-1.5">
                    <Tick ok={!!d.nilKind} />
                    <TaxabilitySelect
                      ledgerId={d.ledgerId ?? null} ledgerName={d.ledgerName ?? null}
                      value={d.nilKind ?? null} entityId={entityId} onSaved={onChanged}
                    />
                  </span>
                </td>
                <td className="px-3 py-2">
                  <span className="flex items-center justify-center gap-1.5">
                    <Tick ok={!!d.placeOfSupply} />
                    <span className={d.placeOfSupply ? 'text-ink-700' : 'text-danger-700'}>{d.placeOfSupply || 'not set'}</span>
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-[11px] text-ink-600">{d.supplyType}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {unmapped.length > 0 && (
        <p className="border-t border-danger-200 bg-danger-50 px-5 py-3 text-xs text-danger-800">
          <span className="font-semibold">Set the taxability here, once per ledger.</span>{' '}
          It decides which of Table 8&rsquo;s three columns the amount goes in, and it holds for every later month.
          Until it is set the amount is filed as <span className="font-semibold">exempt</span> — the total is right, the split may not be.
          The place of supply is the party&rsquo;s, and is set on the party ledger.{' '}
          <Link to="/admin/masters?tab=ledgers" className="font-semibold underline">Open Masters</Link>
        </p>
      )}
    </div>
  );
}

function LedgerBalances({ period, entityId, live, onRead }: { period: string; entityId: string | null; live: boolean; onRead?: (b: { cash: LedgerHeads; itc: LedgerHeads } | null) => void }) {
  const [bal, setBal] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const read = useCallback(async () => {
    if (!entityId || !/^\d{6}$/.test(period)) return;
    try {
      const r = await adminFetchJson<any>('/api/gst/ledgers/balance', { query: { entityId: entityId ?? undefined, period } });
      if (r?.ok !== false) { setBal(r.balances ?? null); onRead?.(r.balances?.cash && r.balances?.itc ? { cash: r.balances.cash, itc: r.balances.itc } : null); }
    } catch { /* the button still works */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, period]);

  useEffect(() => { void read(); }, [read]);

  const fetchNow = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await adminFetch('/api/gst/ledgers/balance', { method: 'POST', query: { entityId: entityId ?? undefined, period } });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (out.needsSession) throw new Error('No portal session — open one at the top of this screen, then read the ledgers.');
        throw new Error(out.error || `Could not read the ledgers (HTTP ${res.status})`);
      }
      setBal(out);
      onRead?.(out?.cash && out?.itc ? { cash: out.cash, itc: out.itc } : null);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  const HEADS: [string, 'igst' | 'cgst' | 'sgst' | 'cess'][] = [['IGST', 'igst'], ['CGST', 'cgst'], ['SGST', 'sgst'], ['Cess', 'cess']];

  return (
    <div className="rounded-2xl border border-ink-200 bg-paper px-5 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-ink-900">What this is paid out of</p>
          <p className="mt-0.5 text-xs text-ink-500">
            The credit ledger goes first, under GSTN&rsquo;s utilisation rules, and cash covers the rest.
            {bal?.readAt ? ` Read ${new Date(bal.readAt).toLocaleString('en-IN')}.` : ''}
          </p>
        </div>
        <button onClick={fetchNow} disabled={busy || !live}
          title={live ? undefined : 'Needs an open portal session'}
          className="inline-flex shrink-0 items-center gap-2 rounded-full border border-ink-300 bg-paper px-4 py-2 text-xs font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {busy ? 'Reading…' : bal ? 'Read again' : 'Read the ledgers'}
        </button>
      </div>

      {err && <p className="mt-3 rounded-xl border border-danger-200 bg-danger-50 px-3.5 py-2.5 text-xs text-danger-700">{err}</p>}

      {!bal && !err && (
        <p className="mt-3 text-xs text-ink-500">
          Not read yet. {live ? 'Read them to see what is available before the offset.' : 'Open a portal session first — this only reads, and moves nothing.'}
        </p>
      )}

      {bal && (
        <>
          <div className="mt-3 overflow-x-auto rounded-xl border border-ink-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-200 text-left text-[11px] uppercase tracking-wide text-ink-400">
                  <th className="px-4 py-2 font-medium">Ledger</th>
                  {HEADS.map(([l]) => <th key={l} className="px-4 py-2 text-right font-medium">{l}</th>)}
                  <th className="px-4 py-2 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-ink-100">
                  <td className="px-4 py-2.5 text-ink-800">Credit (ITC) available</td>
                  {HEADS.map(([l, k]) => <td key={l} className="px-4 py-2.5 text-right tabular-nums text-ink-800">{inr(bal.itc?.[k] ?? 0)}</td>)}
                  <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-ink-900">{inr(bal.itc?.total ?? 0)}</td>
                </tr>
                <tr className="border-b border-ink-100">
                  <td className="px-4 py-2.5 text-ink-800">Cash available</td>
                  {HEADS.map(([l, k]) => <td key={l} className="px-4 py-2.5 text-right tabular-nums text-ink-800">{inr(bal.cash?.[k] ?? 0)}</td>)}
                  <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-ink-900">{inr(bal.cash?.total ?? 0)}</td>
                </tr>
                {/* Blocked credit sits in the ledger and cannot be used.
                    Shown only when there is some, and never added in. */}
                {!!bal.blocked?.total && (
                  <tr>
                    <td className="px-4 py-2.5 text-danger-700">Credit blocked by GSTN — not usable</td>
                    {HEADS.map(([l, k]) => <td key={l} className="px-4 py-2.5 text-right tabular-nums text-danger-700">{inr(bal.blocked?.[k] ?? 0)}</td>)}
                    <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-danger-700">{inr(bal.blocked?.total ?? 0)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11px] text-ink-500">
            Balances as GSTN held them when read, not a set-off. Which credit pays which head, and in what order, is GSTN&rsquo;s
            computation at the moment of offset — the portal does it, and this figure can move if anything is filed in between.
          </p>
        </>
      )}
    </div>
  );
}

/* ── GSTR-1 view ────────────────────────────────────────────────────────── */

/**
 * GSTR-1 — read the return, then file it.
 *
 * Restructured from a flat stack of eight cards. The old order put the filing
 * machinery first — Download JSON top-right, then prepare, then the portal
 * steps — and left the actual numbers below the fold, under everything. A CA
 * checks the figures before filing, so the figures come first and the page now
 * reads as one sequence rather than a pile of panels.
 *
 * Blocking problems are collected into a single band instead of being repeated
 * as three separate banners saying the same thing, and they GATE the filing
 * step rather than sitting next to it as advice.
 */
function Gstr1View({ r, entityId, session, onReload }: {
  r: Returns; entityId: string | null; session: SessionInfo | null;
  /** Re-read the return after a master is corrected, so the blockers above
   *  reflect the fix rather than the state that prompted it. */
  onReload: () => void;
}) {
  const g = r.gstr1; const s = g.summary;

  /* Read from GSTN by the filing card below and lifted here, so "check the
     return" above it can lock against the same fact without a second call to
     the portal for a summary we already have. */
  const [portalHolds, setPortalHolds] = useState(false);

  /* What stops this return being filed, worst first. Only genuine blockers —
     things that make the return wrong or that GSTN will reject. */
  const blockers: {
    title: string; detail: string; fix?: { label: string; to: string };
    /** The documents behind this blocker, where naming them is what a client
     *  needs. A code is not actionable; a bill number is. */
    bills?: { number: string; date: string; party: string | null; hsn: string; taxableValue: number; items?: string[] }[];
  }[] = [];
  if (!r.gstin) blockers.push({
    title: 'No GSTIN on this client',
    detail: 'A return is filed per GSTIN. Without it, interstate versus intrastate cannot be decided, so the B2CL and B2CS split is provisional.',
    fix: { label: 'Add the registration', to: '/admin/masters?tab=company' },
  });
  if (!g.fp) blockers.push({ title: 'No period selected', detail: 'Pick the month this return covers from the period bar above.' });
  if (r.unassignedDocuments) blockers.push({
    title: `${r.unassignedDocuments} document${r.unassignedDocuments > 1 ? 's' : ''} fit no document series`,
    detail: `${(r.unassignedDocs ?? []).map((g) => `${g.count} ${g.type} (${g.samples.slice(0, 3).join(', ')}${g.count > 3 ? ', …' : ''})`).join('; ') || 'They'} — left out of Table 13. Add or correct a series under Masters → Document series.`,
  });
  if (g.crossCheck && !g.crossCheck.ok) blockers.push({
    title: 'Section totals do not match the HSN summary',
    detail: 'The two halves of the return disagree. GSTN validates them against each other, so this would be rejected.',
  });
  /* Table 13 has been mandatory since May 2025, and with no series defined it
     is empty — GSTN refuses the return. The panel below already said so; this
     band said "Nothing blocking" directly above it, because the series check
     counts documents that fit NO series and a client with no series at all
     has none to count. */
  if (!(g.portal?.doc_issue?.doc_det ?? []).length) blockers.push({
    title: 'No document series defined',
    detail: 'Table 13 reports every series issued in the period and is mandatory since May 2025. With none defined it goes up empty, and GSTN refuses the return.',
    fix: { label: 'Add a series', to: '/admin/masters?tab=series' },
  });

  /* An HSN code absent altogether, as against one GSTN does not recognise:
     different faults, and only the second was being reported. GSTN validates
     Table 12 against Tables 4/5/6/7, and a blank hsn_sc fails outright. */
  const noHsn = [...(g.portal?.hsn?.hsn_b2b ?? []), ...(g.portal?.hsn?.hsn_b2c ?? [])]
    .filter((h: any) => !String(h?.hsn_sc ?? '').trim());
  if (noHsn.length) blockers.push({
    title: `No HSN/SAC on ₹${inr(noHsn.reduce((t: number, h: any) => t + Number(h?.txval ?? 0), 0))} of this return`,
    detail: `${noHsn.length} HSN summary row${noHsn.length > 1 ? 's carry' : ' carries'} no code at all. GSTN rejects a Table 12 row with a blank HSN, so the whole return goes back. Set the HSN on the sales ledgers or stock items these bills use.`,
    fix: { label: 'Open Masters', to: '/admin/masters?tab=items' },
  });

  /* GSTN refuses the WHOLE return for one HSN shorter than the taxpayer's
     turnover requires — RET191350, "Length of entered HSN code is not valid
     as per AATO". Caught here instead of on rejection. */
  const shortHsn = (g as any).hsnLength;
  if (shortHsn?.rows?.length) blockers.push({
    title: `${shortHsn.rows.length} HSN code${shortHsn.rows.length > 1 ? 's are' : ' is'} too short — ${shortHsn.required} digits needed`,
    detail: `${shortHsn.rows.map((h: any) => `${h.hsn} (${h.digits} digits, ${h.description || 'no description'}, ₹${inr(h.taxableValue)})`).join('; ')}. `
      + (shortHsn.turnover
        ? `Turnover last year was ₹${inr(shortHsn.turnover)}, so GSTN requires ${shortHsn.required} digits — above ₹5 crore it is 6, otherwise 4. `
        : 'Last year\u2019s turnover is not recorded, so this reads the 4-digit bar. Set it in Masters if the firm is above ₹5 crore. ')
      + 'GSTN rejects the whole return for one short code.',
    fix: { label: 'Open Masters', to: '/admin/masters?tab=items' },
    bills: shortHsn.bills ?? [],
  });

  if (r.unknownHsnCodes?.length) blockers.push({
    title: `${r.unknownHsnCodes.length} HSN/SAC code${r.unknownHsnCodes.length > 1 ? 's are' : ' is'} not in the GSTN master`,
    detail: `${r.unknownHsnCodes.join(', ')} — descriptions are left blank rather than invented. Load the master, or correct the code on the ledger.`,
    fix: { label: 'HSN master', to: '/admin/masters?tab=items' },
  });

  return (
    <div className="space-y-8">
      {/* ── 1 · the return ─────────────────────────────────────────────── */}
      <section className="space-y-4">
        <Band n={1} title="The return" hint={`GSTIN ${r.gstin ?? '— not set'} · period ${g.fp || '— not selected'}`} />

        <div className="grid gap-3 sm:grid-cols-3">
          <Tile label="Total documents" value={String(s.totalDocs)} />
          <Tile label="Taxable value" value={`₹${inr(s.taxableValue)}`} />
          <Tile label="Total tax" value={`₹${inr(s.totalTax)}`} />
        </div>

        <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-paper">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-400">
                <th className="px-5 py-3 font-medium">Section</th><th className="px-5 py-3 text-right font-medium">Docs</th>
                <th className="px-5 py-3 text-right font-medium">Taxable value</th><th className="px-5 py-3 text-right font-medium">Tax</th>
              </tr>
            </thead>
            <tbody>
              {s.sections.map((sec) => (
                <tr key={sec.code} className={`border-b border-ink-100 last:border-0 ${sec.count === 0 ? 'opacity-40' : ''}`}>
                  <td className="px-5 py-3"><span className="font-mono text-xs uppercase text-ink-400">{sec.code}</span> <span className="ml-2 text-ink-800">{sec.label}</span></td>
                  <td className="px-5 py-3 text-right tabular-nums text-ink-500">{sec.count}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-ink-800">₹{inr(sec.taxableValue)}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-ink-600">₹{inr(sec.tax)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <HsnSummary portal={g.portal} />
        <NilSupplies nil={(g as any).nilSupplies} entityId={entityId} onChanged={onReload} />
      </section>

      {/* ── 2 · what the portal already holds ──────────────────────────── */}
      <PortalDraftPanel entityId={entityId} period={g.fp} session={session} n={2} />

      {/* ── 3 · what to fix ────────────────────────────────────────────── */}
      <section className="space-y-4">
        <Band
          n={3}
          title={blockers.length ? `Fix before filing — ${blockers.length}` : 'Nothing blocking'}
          hint={blockers.length ? 'GSTN validates these; the return would be wrong or rejected.' : 'No blocking problems found in this return.'}
          tone={blockers.length ? 'danger' : 'ok'}
        />
        {blockers.length === 0 ? (
          <div className="flex items-center gap-2 rounded-2xl border border-ok-200 bg-ok-50/60 px-5 py-4 text-sm text-ok-700">
            <CheckCircle2 className="h-4 w-4 shrink-0" /> Ready to file.
          </div>
        ) : (
          <div className="divide-y divide-ink-100 overflow-hidden rounded-2xl border border-danger-200 bg-paper">
            {blockers.map((b) => (
              <div key={b.title} className="flex items-start gap-3 px-5 py-4">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-danger-600" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-ink-900">{b.title}</p>
                  <p className="mt-0.5 text-sm text-ink-600">{b.detail}</p>
                  {/* The bills themselves. A client cannot act on "1006 is too
                      short"; they can act on "bill 85 of 2 October". */}
                  {!!b.bills?.length && (
                    <div className="mt-3 overflow-x-auto rounded-xl border border-ink-200">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-ink-200 bg-ink-50/60 text-left uppercase tracking-wide text-ink-500">
                            <th className="px-3 py-2 font-medium">Bill</th><th className="px-3 py-2 font-medium">Date</th>
                            <th className="px-3 py-2 font-medium">Party</th><th className="px-3 py-2 font-medium">Set on</th>
                            <th className="px-3 py-2 font-medium">HSN</th>
                            <th className="px-3 py-2 text-right font-medium">Taxable value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {b.bills.map((bill, i) => (
                            <tr key={`${bill.number}-${bill.hsn}-${i}`} className="border-b border-ink-100 last:border-0">
                              <td className="px-3 py-2 font-mono text-ink-900">{bill.number || '\u2014'}</td>
                              <td className="px-3 py-2 text-ink-600">{bill.date || '\u2014'}</td>
                              <td className="px-3 py-2 text-ink-700">{bill.party ?? '\u2014'}</td>
                              {/* The master to open. Without it the code is a
                                  dead end — it is set on an item or a ledger,
                                  never on the bill itself. */}
                              <td className="px-3 py-2 text-ink-700">{bill.items?.length ? bill.items.join(', ') : '\u2014'}</td>
                              <td className="px-3 py-2 font-mono text-danger-700">{bill.hsn}</td>
                              <td className="px-3 py-2 text-right font-mono text-ink-700">{`\u20b9${inr(bill.taxableValue)}`}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                {b.fix && (
                  <Link to={b.fix.to} className="shrink-0 rounded-full border border-ink-300 bg-paper px-3 py-1.5 text-xs font-semibold text-ink-700 no-underline hover:bg-ink-50">
                    {b.fix.label}
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
        <CrossCheckPanel crossCheck={g.crossCheck} />
        <DocIssueSummary portal={g.portal} unassigned={r.unassignedDocuments} unassignedDocs={r.unassignedDocs} notes={r.table13Notes} />
      </section>

      {/* ── 4 · prepare ────────────────────────────────────────────────── */}
      <section className="space-y-4">
        <Band n={4} title="Prepare" hint="Build the return and run GSTN\u2019s validations over it. Nothing is sent." />
        {blockers.length > 0 && (
          <p className="rounded-xl border border-warn-200 bg-warn-50 px-4 py-2.5 text-xs text-warn-800">
            Filing is still available, but the problems above are unresolved — check them first.
          </p>
        )}
        <FilingPanel returnType="GSTR1" entityId={entityId} part="4" portalHolds={portalHolds} />
      </section>

      {/* ── 5 · file it ────────────────────────────────────────────────── */}
      <section className="space-y-4">
        <Band n={5} title="File it" hint="Send it to the portal, sign it with the client's OTP, and file. The last step cannot be undone." />
        <FileReturnPanel entityId={entityId} period={g.fp} returnType="GSTR1" session={session} part="5"
          onPortalState={setPortalHolds} />
      </section>
    </div>
  );
}

/** Numbered band heading — turns a stack of cards into a sequence. */
function Band({ n, title, hint, tone = 'neutral' }: { n: number; title: string; hint?: string; tone?: 'neutral' | 'danger' | 'ok' }) {
  const dot = tone === 'danger' ? 'bg-danger-600 text-white' : tone === 'ok' ? 'bg-ok-600 text-white' : 'bg-inkblack text-highlighter';
  return (
    <div className="flex items-start gap-3">
      <span className={`grid size-6 shrink-0 place-items-center rounded-full text-xs font-bold ${dot}`}>{n}</span>
      <div className="min-w-0">
        <h3 className="text-base font-semibold leading-6 text-ink-900">{title}</h3>
        {hint && <p className="mt-0.5 text-xs text-ink-500">{hint}</p>}
      </div>
    </div>
  );
}

/* ── GSTR-3B view ───────────────────────────────────────────────────────── */

type LedgerHeads = { igst: number; cgst: number; sgst: number; cess: number };

interface PortalCompare {
  ok: boolean; error?: string; needsSession?: boolean;
  period?: string; arn?: string | null; status?: string | null;
  comparison?: { table: string; label: string; ours: number; portal: number; diff: number }[] | null;
}

/**
 * The 3B as the form actually looks, table by table.
 *
 * A summary of four lines is fine until someone has to tie it to what the
 * portal shows, and then every row matters — 3.1(c) against 3.1(a) is where
 * ₹51,31,640 of exempt sales hid. So this mirrors the return: 3.1, 3.1.1, 3.2,
 * the whole of 4, 5 and 5.1, with the figures we cannot derive shown as the
 * zeros they are and listed separately as needing a person.
 */
function Gstr3bView({ r, entityId, session, onOpen2b }: { r: Returns; entityId: string | null; session: SessionInfo | null; onOpen2b: () => void }) {
  /* GSTN's 5.1, once asked for. Held here so the table above and the card
     below show the same figures. */
  const [liab, setLiab] = useState<any>(null);
  /* The credit and cash ledgers once read, so 6.1 can stop being indicative. */
  const [ledgers, setLedgers] = useState<{ cash: LedgerHeads; itc: LedgerHeads } | null>(null);
  const g = r.gstr3b;
  const t = g.itcTable;
  const p = g.portal ?? {};
  const from2b = g.itcSource === '2b';
  const [cmp, setCmp] = useState<PortalCompare | null>(null);
  const [busy, setBusy] = useState(false);

  const blockers: { title: string; detail: string; fix?: { label: string; to: string }; act?: { label: string; on: () => void } }[] = [];
  if (!from2b) blockers.push({
    title: 'Input credit is from the books, not GSTR-2B',
    detail: 'Credit can only be claimed as far as it appears in 2B (Section 16(2)(aa)). Table 4 below is an estimate until this period’s 2B is loaded.',
    act: { label: 'Load the 2B', on: onOpen2b },
  });
  if (!r.gstin) blockers.push({
    title: 'No GSTIN on this client',
    detail: 'A return is filed per GSTIN. Without it the return has no header, and interstate versus intrastate cannot be decided.',
    fix: { label: 'Add the registration', to: '/admin/masters?tab=company' },
  });
  if (!g.fp) blockers.push({ title: 'No period selected', detail: 'Pick the month this return covers from the period bar above.' });

  const n = (x: number | undefined | null) => inr(Number(x ?? 0));
  const compare = async () => {
    setBusy(true);
    try {
      const out = await adminFetchJson<PortalCompare>(`/api/gst/portal/filed?period=${encodeURIComponent(g.fp)}&type=GSTR3B`, {
        method: 'POST', query: { entityId: entityId ?? undefined },
      });
      setCmp(out);
      if (out.ok === false) toast.error(out.error ?? 'Could not reach the portal');
      else toast.success(out.arn ? `Portal returned the return filed under ARN ${out.arn}` : 'Fetched what the portal holds');
    } catch (e) {
      toast.error((e as Error).message.replace(/^\d+\s/, ''));
    } finally { setBusy(false); }
  };
  const differences = (cmp?.comparison ?? []).filter((x) => Math.abs(x.diff) > 0.5);

  return (
    <div className="space-y-8">
      {/* ── 1 · the return ─────────────────────────────────────────────── */}
      <section className="space-y-4">
        <Band n={1} title="The return" hint={`GSTIN ${r.gstin ?? '— not set'} · period ${g.fp || '— not selected'}`} />

        <div className="grid gap-3 sm:grid-cols-3">
          <Tile label="Output tax, 3.1(a)" value={`₹${n(Number(p?.sup_details?.osup_det?.iamt ?? 0) + Number(p?.sup_details?.osup_det?.camt ?? 0) + Number(p?.sup_details?.osup_det?.samt ?? 0))}`} sub={`on ₹${n(p?.sup_details?.osup_det?.txval)}`} />
          <Tile label="Input credit, 4C" value={`₹${n(t ? t.net.igst + t.net.cgst + t.net.sgst : 0)}`} sub={from2b ? 'as GSTR-2B supports it' : 'from the books — an estimate'} tone={from2b ? 'ok' : 'warn'} />
          <Tile label="Net payable" value={`₹${n(g.netTaxPayable.igst + g.netTaxPayable.cgst + g.netTaxPayable.sgst)}`} sub="indicative, before set-off order" />
        </div>

        <Gstr3bEditor
          portal={p} gstin={r.gstin} period={g.fp}
          twoB={from2b && t ? t.oth : null}
          gstn51={liab && (liab.interest || liab.lateFee) ? { interest: liab.interest ?? null, lateFee: liab.lateFee ?? null } : null}
          ledgers={ledgers}
        />

        {/* 5.1 is GSTN's computation, not ours: we filled it with zeroes and a
            note saying so, and a client's own draft showed ₹228.54 of interest
            and ₹200 of late fee our screen could not show. */}
        <Gstn51 period={g.fp} entityId={entityId} live={!!session?.session} liab={liab} setLiab={setLiab} />

        {/* What the return is actually paid out of. Having both endpoints and
            showing neither is what made "we need the cash and credit ledger
            balances" a trip to the portal. */}
        <LedgerBalances period={g.fp} entityId={entityId} live={!!session?.session} onRead={setLedgers} />

        {/* 6.1 in practice. The offset is what settles the return, and until
            now it was the one step oddits sent people to the portal for. */}
        <OffsetLiability period={g.fp} entityId={entityId} live={!!session?.session} />
      </section>

      {/* ── 2 · against the portal ─────────────────────────────────────── */}
      <section className="space-y-4">
        <Band n={2} title="Against the portal" hint="Fetch what GSTN holds for this period and compare it line by line. For a filed period, that is the filed return." />
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-ink-200 bg-paper px-5 py-4">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink-900">What does GSTN hold?</p>
            <p className="mt-0.5 text-xs text-ink-500">
              Needs an open portal session. Nothing is sent — this only reads, and records the ARN when the portal returns one.
            </p>
          </div>
          <button onClick={compare} disabled={busy || !g.fp}
            className="inline-flex items-center gap-2 rounded-full border border-ink-300 bg-paper px-5 py-2.5 text-sm font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {busy ? 'Asking the portal…' : 'Compare with the portal'}
          </button>
        </div>

        {cmp?.ok && cmp.comparison && (
          differences.length === 0 ? (
            <div className="flex items-center gap-2 rounded-2xl border border-ok-200 bg-ok-50/60 px-5 py-4 text-sm text-ok-700">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              Every line agrees with the portal{cmp.arn ? `, filed under ARN ${cmp.arn}` : ''}.
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-warn-200 bg-paper">
              <div className="border-b border-ink-100 bg-warn-50/60 px-5 py-3">
                <p className="text-sm font-semibold text-ink-900">{differences.length} line(s) differ from the portal</p>
                <p className="text-xs text-ink-600">{cmp.arn ? `Filed under ARN ${cmp.arn}. ` : ''}Ours on the left, GSTN's on the right — theirs is the one that was filed.</p>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-ink-100 text-left text-xs uppercase tracking-wide text-ink-400">
                    <th className="px-5 py-2 font-medium">Table</th><th className="px-5 py-2 font-medium" />
                    <th className="px-5 py-2 text-right font-medium">Ours</th><th className="px-5 py-2 text-right font-medium">Portal</th><th className="px-5 py-2 text-right font-medium">Difference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-50">
                  {differences.map((x, i) => (
                    <tr key={i}>
                      <td className="px-5 py-2 font-mono text-xs text-ink-500">{x.table}</td>
                      <td className="px-5 py-2 text-ink-800">{x.label}</td>
                      <td className="px-5 py-2 text-right tabular-nums text-ink-800">{n(x.ours)}</td>
                      <td className="px-5 py-2 text-right tabular-nums text-ink-800">{n(x.portal)}</td>
                      <td className="px-5 py-2 text-right tabular-nums font-medium text-danger-700">{n(Math.abs(x.diff))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {blockers.length === 0 ? (
          <div className="flex items-center gap-2 rounded-2xl border border-ok-200 bg-ok-50/60 px-5 py-4 text-sm text-ok-700">
            <CheckCircle2 className="h-4 w-4 shrink-0" /> Nothing blocking this return.
          </div>
        ) : (
          <div className="divide-y divide-ink-100 overflow-hidden rounded-2xl border border-danger-200 bg-paper">
            {blockers.map((b) => (
              <div key={b.title} className="flex items-start gap-3 px-5 py-4">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-danger-600" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-ink-900">{b.title}</p>
                  <p className="mt-0.5 text-sm text-ink-600">{b.detail}</p>
                </div>
                {b.fix && <Link to={b.fix.to} className="shrink-0 rounded-full border border-ink-300 bg-paper px-3 py-1.5 text-xs font-semibold text-ink-700 no-underline hover:bg-ink-50">{b.fix.label}</Link>}
                {b.act && <button onClick={b.act.on} className="shrink-0 rounded-full border border-ink-300 bg-paper px-3 py-1.5 text-xs font-semibold text-ink-700 hover:bg-ink-50">{b.act.label}</button>}
              </div>
            ))}
          </div>
        )}
        <ManualEntryPanel items={g.needsManualEntry} />
      </section>

      {/* ── 3 · file it ────────────────────────────────────────────────── */}
      <section className="space-y-4">
        <Band n={3} title="File it" hint="Two parts: check the return here, then sign and send it to the portal below." />
        {blockers.length > 0 && (
          <p className="rounded-xl border border-warn-200 bg-warn-50 px-4 py-2.5 text-xs text-warn-800">
            Filing is still available, but the problems above are unresolved — check them first.
          </p>
        )}
        {/* Bills in 2B but not in the books are never held back on their own,
            so the return is not filed until each has been decided. Preparing
            and previewing it still work. */}
        {!!g.pending2b?.count && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-800">
            <span>
              <span className="font-semibold">Resolve {g.pending2b.count} bill{g.pending2b.count === 1 ? '' : 's'} on 2B recon</span>
              {' '}— in 2B but not in the books (₹{inr(g.pending2b.tax)} of credit). Record, hold back or claim each one; filing waits until then.
            </span>
            <button onClick={onOpen2b} className="shrink-0 rounded-full border border-danger-300 bg-paper px-3 py-1.5 text-xs font-semibold text-danger-700 hover:bg-danger-50">Open 2B recon</button>
          </div>
        )}
        <FilingPanel returnType="GSTR3B" entityId={entityId} part="3a" />
        <FileReturnPanel entityId={entityId} period={g.fp} returnType="GSTR3B" session={session} part="3b"
          fileBlocked={g.pending2b?.count ? `Resolve ${g.pending2b.count} bill${g.pending2b.count === 1 ? '' : 's'} on 2B recon before filing.` : null} />
      </section>
    </div>
  );
}

/* ── shared bits ────────────────────────────────────────────────────────── */


/**
 * A document's rate — or its rates. An invoice with 5% and 18% items shows
 * both, with the value at each on hover and the number of lines beneath, and
 * never a single blended figure: 6.34% is not a rate anyone charged. One that
 * could not be split says so instead of showing a number.
 */
function RateCell({ d }: { d: GstDoc }) {
  if (d.rateSource === 'unresolved') {
    return <span className="rounded-full bg-danger-50 px-2 py-0.5 text-[11px] font-medium text-danger-700" title="The tax charged is not a single GST rate and no item or ledger rates explain it.">needs split</span>;
  }
  const rates = [...new Set((d.lines ?? []).map((l) => l.rate))].sort((a, b) => a - b);
  if (rates.length > 1) {
    const title = rates.map((r) => {
      const at = (d.lines ?? []).filter((l) => l.rate === r);
      return `${r}%: ₹${inr(at.reduce((t, l) => t + l.taxableValue, 0))} taxable, ₹${inr(at.reduce((t, l) => t + l.cgst + l.sgst + l.igst + l.cess, 0))} tax`;
    }).join('\n') + (d.rateSource === 'inferred' ? '\nWorked out from the tax charged — check against the invoice.' : '');
    return (
      <span title={title} className="inline-flex flex-col items-center leading-tight">
        <span className="whitespace-nowrap">{rates.map((r) => `${r}%`).join(' + ')}</span>
        <span className="text-[10px] text-ink-400">{rates.length} lines{d.rateSource === 'inferred' ? ' · worked out' : ''}</span>
      </span>
    );
  }
  return <>{d.rate == null ? '—' : `${d.rate}%`}</>;
}

function Tab({ active, onClick, icon: Icon, label }: { active: boolean; onClick: () => void; icon: typeof ArrowUpRight; label: string }) {
  return (
    <button onClick={onClick} className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition ${active ? 'bg-inkblack text-white' : 'text-ink-600 hover:bg-ink-50'}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'warn' | 'ok' }) {
  const ring = tone === 'warn' ? 'border-warn-200 bg-warn-50/50' : tone === 'ok' ? 'border-ok-200 bg-ok-50/50' : 'border-ink-200 bg-paper';
  return (
    <div className={`rounded-2xl border p-4 ${ring}`}>
      <p className="text-xs font-medium text-ink-500">{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums text-ink-900">{value}</p>
      {sub && <p className="mt-1 text-[11px] text-ink-500">{sub}</p>}
    </div>
  );
}

function Breakdown({ title, rows }: { title: string; rows: { label: string; count: number; taxable: number; tax: number }[] }) {
  return (
    <div className="rounded-2xl border border-ink-200 bg-paper p-4">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">{title}</p>
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r) => (
            <tr key={r.label} className="border-b border-ink-100 last:border-0">
              <td className="py-2 font-medium text-ink-700">{r.label}</td>
              <td className="py-2 text-right text-xs text-ink-400">{r.count} docs</td>
              <td className="py-2 text-right tabular-nums text-ink-700">₹{inr(r.taxable)}</td>
              <td className="py-2 pl-3 text-right tabular-nums text-ink-500">₹{inr(r.tax)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Empty({ label }: { label: string }) {
  return (
    <div className="rounded-2xl border border-ink-200 bg-paper px-6 py-16 text-center">
      <h3 className="text-lg font-semibold text-ink-900">No {label} documents</h3>
      <p className="mt-1.5 text-sm text-ink-600">Import Tally data for this entity and period to see its GST data.</p>
    </div>
  );
}


/* ── what GSTN already holds for an unfiled period ───────────────────────── */

interface DraftRow { gstin: string | null; number: string | null; date: string | null; taxableValue: number; tax: number }
interface DraftCompare {
  fetchedAt: string | null; portalCount: number; booksCount: number;
  matched: { gstin: string; number: string; ours: DraftRow; portal: DraftRow; taxDifference: number }[];
  numberedDifferently: { gstin: string; ours: DraftRow; portal: DraftRow }[];
  onlyOurs: DraftRow[]; onlyPortal: DraftRow[];
}

/**
 * An e-invoice is on the portal before we are. Generating an IRN auto-populates
 * GSTR-1, and saving over it only replaces a record when the document number
 * matches exactly — otherwise the sale is declared twice. A save also never
 * removes what it does not send. Neither is visible anywhere else, so this is
 * the one place to look before pressing anything.
 */
function PortalDraftPanel({ entityId, period, session, n }: { entityId: string | null; period: string; session: SessionInfo | null; n: number }) {
  const [data, setData] = useState<DraftCompare | null>(null);
  const [busy, setBusy] = useState(false);
  const open = !!session?.session?.minutesLeft;

  const load = useCallback(() => {
    if (!entityId || !period) return;
    adminFetchJson<DraftCompare>('/api/gst/portal/gstr1-draft', { query: { entityId, period } })
      .then(setData).catch(() => setData(null));
  }, [entityId, period]);
  useEffect(load, [load]);

  const check = async () => {
    setBusy(true);
    try {
      const r = await adminFetchJson<any>('/api/gst/portal/gstr1-draft', {
        method: 'POST', query: { entityId: entityId ?? undefined, period },
      });
      if (r?.ok === false) throw new Error(r.error ?? 'The portal did not answer');
      const errs = (r.sections ?? []).filter((x: any) => x.error);
      if (errs.length) toast.error(errs.map((x: any) => `${x.section}: ${x.error}`).join(' · '));
      else toast.success(r.records ? `${r.records} document(s) already on the portal.` : 'The portal holds nothing for this period yet.');
      load();
    } catch (e) { toast.error((e as Error).message.replace(/^\d+\s/, '')); }
    finally { setBusy(false); }
  };

  const risky = (data?.numberedDifferently.length ?? 0) + (data?.onlyPortal.length ?? 0);

  return (
    <section className="space-y-4">
      <Band
        n={n}
        title="What the portal already holds"
        hint="E-invoices reach GSTN when their IRN is generated, not when you file. Saving over a record only replaces it when the document number matches exactly."
        tone={risky ? 'danger' : data ? 'ok' : undefined}
      />
      <div className="rounded-2xl border border-ink-200 bg-paper">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
          <div className="min-w-0">
            {data?.fetchedAt ? (
              <>
                <p className="text-sm text-ink-800">
                  <span className="font-semibold">{data.portalCount}</span> document(s) on the portal ·
                  <span className="ml-1 font-semibold">{data.booksCount}</span> in the books
                </p>
                <p className="mt-0.5 text-[11px] text-ink-400">Checked {new Date(data.fetchedAt).toLocaleString('en-IN')}</p>
              </>
            ) : (
              <p className="text-sm text-ink-600">Not checked yet. Nothing is sent to the portal — this only reads.</p>
            )}
          </div>
          <ConfirmAction
            label={data?.fetchedAt ? 'Check again' : 'Check the portal'}
            disabled={busy || !open || !period}
            title={open ? 'Read the unfiled GSTR-1 from GSTN' : 'Needs an open portal session'}
            question={`Read the unfiled GSTR-1 for ${period} from the portal? Nothing is saved or filed there.`}
            confirmLabel={busy ? 'Reading…' : 'Read it'}
            onRun={check}
          />
        </div>

        {!open && (
          <p className="border-t border-ink-100 px-5 py-3 text-xs text-ink-500">
            Needs an open portal session.{' '}
            <button onClick={focusPortalSession} className="font-medium text-ink-700 underline">Open one</button>
          </p>
        )}

        {data?.fetchedAt && (
          <div className="divide-y divide-ink-100 border-t border-ink-100">
            <DraftGroup
              tone="danger"
              title={`Same bill, different number — ${data.numberedDifferently.length}`}
              hint="The portal already has this sale under another number. Saving ours ADDS a second record, so it is declared twice."
              rows={data.numberedDifferently.map((d) => ({
                key: `${d.gstin}-${d.ours.number}`, gstin: d.gstin,
                left: d.ours.number ?? '—', right: d.portal.number ?? '—', amount: d.ours.taxableValue,
              }))}
              leftLabel="ours" rightLabel="on the portal"
            />
            <DraftGroup
              tone="warn"
              title={`On the portal, not in the books — ${data.onlyPortal.length}`}
              hint="A save does not remove these. They will be filed as they stand."
              rows={data.onlyPortal.map((d, i) => ({ key: `${d.gstin}-${d.number}-${i}`, gstin: d.gstin ?? '—', left: '—', right: d.number ?? '—', amount: d.taxableValue }))}
              leftLabel="ours" rightLabel="on the portal"
            />
            <DraftGroup
              tone="ok"
              title={`Same number on both — ${data.matched.length}`}
              hint="A save replaces these records rather than adding to them."
              rows={data.matched.map((m) => ({ key: `${m.gstin}-${m.number}`, gstin: m.gstin, left: m.number, right: m.number, amount: m.ours.taxableValue, note: m.taxDifference ? `tax differs by ₹${inr(Math.abs(m.taxDifference))}` : undefined }))}
              leftLabel="ours" rightLabel="on the portal"
            />
            <DraftGroup
              tone="neutral"
              title={`In the books only — ${data.onlyOurs.length}`}
              hint="Not on the portal yet. A save adds them — the ordinary case for anything not e-invoiced."
              rows={data.onlyOurs.map((d, i) => ({ key: `${d.gstin}-${d.number}-${i}`, gstin: d.gstin ?? '—', left: d.number ?? '—', right: '—', amount: d.taxableValue }))}
              leftLabel="ours" rightLabel="on the portal"
            />
          </div>
        )}
      </div>
    </section>
  );
}

function DraftGroup({ tone, title, hint, rows, leftLabel, rightLabel }: {
  tone: 'danger' | 'warn' | 'ok' | 'neutral'; title: string; hint: string;
  rows: { key: string; gstin: string; left: string; right: string; amount: number; note?: string }[];
  leftLabel: string; rightLabel: string;
}) {
  const [open, setOpen] = useState(tone === 'danger' || tone === 'warn');
  if (!rows.length) return null;
  const bg = { danger: 'bg-danger-50/70', warn: 'bg-warn-50/70', ok: 'bg-ok-50/60', neutral: 'bg-ink-50/70' }[tone];
  const dot = { danger: 'bg-danger-500', warn: 'bg-warn-500', ok: 'bg-ok-500', neutral: 'bg-ink-300' }[tone];
  return (
    <div>
      <button onClick={() => setOpen(!open)} className={`flex w-full items-start gap-2 px-5 py-3 text-left ${bg}`}>
        <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-ink-900">{title}</span>
          <span className="block text-xs text-ink-600">{hint}</span>
        </span>
        <span className="text-[11px] text-ink-400">{open ? 'hide' : 'show'}</span>
      </button>
      {open && (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-y border-ink-100 text-left text-[11px] uppercase tracking-wide text-ink-400">
              <th className="px-5 py-2 font-medium">Counterparty</th>
              <th className="px-5 py-2 font-medium">{leftLabel}</th>
              <th className="px-5 py-2 font-medium">{rightLabel}</th>
              <th className="px-5 py-2 text-right font-medium">Taxable</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 50).map((r) => (
              <tr key={r.key} className="border-b border-ink-50 last:border-0">
                <td className="px-5 py-2 font-mono text-[11px] text-ink-500">{r.gstin}</td>
                <td className="px-5 py-2 font-mono text-ink-800">{r.left}</td>
                <td className="px-5 py-2 font-mono text-ink-800">{r.right}</td>
                <td className="px-5 py-2 text-right tabular-nums text-ink-600">
                  ₹{inr(r.amount)}
                  {r.note && <span className="block text-[11px] text-warn-700">{r.note}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {rows.length > 50 && <p className="px-5 py-2 text-[11px] text-ink-400">Showing the first 50 of {rows.length}.</p>}
    </div>
  );
}
